// Virtualized table grid with resizable + reorderable columns.
//
// Props:
//   columns         Column[]                — original column metadata
//   rows            any[][]                 — rows of cells in NATURAL column order
//   displayOrder    string[]                — column names in render order (defaults to natural)
//   widthByName     {[colName]: number}     — override widths (defaults applied for unknown names)
//   pinnedNames     string[]                — these columns render sticky-left
//   showTypes       bool                    — show `varchar(50)` next to header name
//   thousandSep     bool                    — format numbers with thousand separators
//   excludedFormat  string[]                — column names opted-OUT of formatting
//   hiddenNames     string[]                — columns marked hidden; only present in
//                                             displayOrder when "Show hidden" is on, and
//                                             rendered dimmed so they're recognisable
//   sortKeys        {column,dir:'asc'|'desc'}[]  — current ORDER BY keys, in order
//   startIndex      number    — base for the leftmost row-number gutter (default 0).
//                               Pass the page offset for paginated table data so the
//                               "#" continues across pages; 0 (default) for query results.
//   onSortToggle(colName, ctrlKey) — sort-icon click (callee handles cycle / multi)
//   totalRows       number    — (optional) the unfiltered row count; when it differs from the
//                               passed rows length the selection footer shows "X of Y rows (filtered)".
//   footerRows()    JSX       — (optional) render function whose output replaces the footer's row
//                               count (the Browser's pager). The footer then shows even while
//                               editing; the selection stats on its right stay selectable-only.
//   editable        bool      — when true, cells become navigable + editable. PK cols
//                               (col.isPrimary) and columns flagged col.editable===false
//                               (read-only query-result expression columns) stay read-only.
//                               Enum columns (type `enum('a','b')`) render a <select>.
//   selectable      bool      — read-only cell selection + copy (query results only; ignored when
//                               editable). Plain click selects a cell; drag / Shift-click extend a
//                               rectangular range; Ctrl/Cmd+click jumps an FK to its parent;
//                               Ctrl/Cmd+Shift+click copies the clicked cell; Ctrl/Cmd+C copies the
//                               range as TSV. A right-click menu adds copy column / copy row / stats.
//                               A `selectable` grid OWNS its right-click menu (onHeaderContextMenu/
//                               onCellContextMenu are not used); a non-selectable grid defers
//                               right-click to those handlers instead.
//   extraHeaderItems(col)         — (selectable only) extra ContextMenu items appended after a
//                               divider to the internal header copy menu (e.g. Pin/Hide/Edit-table).
//   extraCellItems(col, rowIdx)   — (selectable only) same, for the cell copy menu.
//   onRowEdit(rowIdx, changes, { row }) — fires when the cursor leaves a row that has buffered
//                                edits. `changes` is { colName: newValue }. Typing NULL sends JS
//                                null; the TEXT 'NULL' is edited as `'NULL'` (see editTextOf).
//                                `row` is the row the edit was made on — use it (not rowIdx,
//                                which may be stale after a sort/filter) to find the key.
//                                Same fires on flush(). Return { cancelled: true } to drop
//                                the staged-edit markers (cancelled or failed write).
//   onEditPreview(rowIdx, changes, kind) — optional. Fires live as the user types / moves
//                                between columns (NOT a server write), so the parent can show a
//                                "would-run" preview. kind is 'update' (existing row) or
//                                'insert' (a draft/new row). `changes` is {} when nothing differs.
//   onDraftsChange(list) — optional. Fires with [{key, values}] for every pending draft row
//                          (focused draft's in-progress edit merged in) so the parent can show
//                          all pending INSERTs in its log; [] when there are none.
//   onRowInsert(values) — optional. Presence ENABLES new-row drafts (INSERT). Called per draft
//                                at commit-flush; must return 'ok' | 'skipped' | 'aborted'.
//                                `values` is the user-set columns (auto-increment/generated omitted).
//   registerInsertFlusher(fn) — optional. Parent calls fn() before COMMIT to send pending drafts;
//                                returns { validation:[{missing}], aborted } so the parent can
//                                block the commit on a missing required field or a stop-mode abort.
//   registerRowInserter(fn) — optional. Registers an append() the parent's "Append row" button calls.
//   registerDraftClearer(fn) — optional. Registers clearDrafts(); parent calls it on exit / after a
//                                successful commit refetch so drafts don't linger.
//   onDirtyChange(count) — optional. Fires with the number of staged (edited-but-uncommitted)
//                                cells so the host can show an "N changes" bar.
//   registerDirtyClearer(fn) — optional. Registers a clearer the parent calls on COMMIT/ROLLBACK
//                                to drop the staged-edit markers (leaving edit mode clears them too).
//   onRowSelectionChange(idxs) — optional. Fires with the array of currently-selected
//                                row indices (gutter-click selection). Editable grids only — the
//                                parent uses this to show "🗑 Delete N row(s)" in its toolbar.
//                                The emitted indices are always RAW indices into the current
//                                props.rows; they re-derive from getRowKey on every rows-change
//                                so a sort/filter doesn't mis-target the parent's DELETE.
//   registerRowSelectionClearer(fn) — optional. Registers a clearer the parent calls after a
//                                successful bulk-delete (or COMMIT/ROLLBACK) so the selection
//                                doesn't outlive the rows it points at.
//   getRowKey(rowIdx) — optional. Returns a stable string identity for the row at
//                                rowIdx (typically the PK tuple joined by a separator). When
//                                provided, gutter selection is stored BY KEY, so it survives
//                                local sort/filter (display positions move; keys don't) — only
//                                rows whose keys are no longer present drop off. When omitted,
//                                selection falls back to raw-idx storage and clears wholesale on
//                                any rows-identity change (the safe but lossy default).
//   registerFlusher(fn) — optional. The grid registers a flush function the
//                                parent can call before COMMIT/ROLLBACK to make sure pending
//                                edits hit the server first. Returns a Promise.
//   registerAutoFit(fn) — optional. The grid registers a function that measures
//                                the displayed cell content and returns a
//                                { colName: width } map (90th-percentile content
//                                width, clamped + header-floored). The parent applies
//                                it however it persists widths (colprefs / ephemeral).
//   onHeaderClick(col, e)         — left-click (opens filter popover)
//   onHeaderContextMenu(col, e)   — right-click on a header cell (column menu)
//   onCellContextMenu(col, e)     — right-click on a data row cell (table menu)
//   onResize(colName, newWidth)   — committed on mouse-up
//   onReorder(newDisplayOrder)    — committed on drop
import { createSignal, createMemo, createEffect, createComputed, untrack, onCleanup, onMount, createUniqueId, For, Show } from 'solid-js';
import { isNumericType, formatNumberWithSep, temporalKind, formatTemporal, collapsePreview, enumValues, isGeneratedExtra, editTextOf, parseEditText, isBinaryType } from '../lib/format.js';
import { restoreLineEndings } from '../lib/cellvalue.js';
import { copyText, escapeTSVField } from '../lib/export.js';
import { buildJson, buildMarkdown, buildInserts } from '../lib/gridcopy.js';
import { toast } from '../lib/toast.js';
import Modal from './Modal.jsx';
import CellValueViewer, { pickedCell, setPickedCell } from './CellValueViewer.jsx';
import ContextMenu from './ContextMenu.jsx';

// Cells whose text exceeds this length show a collapsed preview + an expand
// button that opens the full value in a modal (read-only, or editable in the
// editable data grid).
const LONG_TEXT = 120;

// Re-export so existing imports (e.g. Browser.jsx) keep working.
export { isNumericType, formatNumberWithSep };

const ROW_HEIGHT = 28;
const OVERSCAN   = 8;
// Keys that move the keyboard active cell in a read-only selectable grid.
const NAV_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown']);

// Default widths (used when no saved pref for a column).
const DEFAULT_WIDTH_FULL    = 180;  // "Types" toggle on
const DEFAULT_WIDTH_COMPACT = 130;  // "Types" toggle off

const MIN_WIDTH = 60;
const MAX_WIDTH = 1000;

// Auto-fit tuning (one-shot column sizing — see computeAutoFitWidths).
const AUTOFIT_SAMPLE    = 200;   // rows measured per column
const AUTOFIT_EMPTY_PCT = 0.5;   // > this share null/empty/zero ⇒ "mostly empty"
const CELL_PAD          = 20;    // .grid-cell horizontal padding (10px × 2)
// Header chrome (the non-label width of a header cell). Split out so the
// filter/sort icons only count when actually rendered — the query-result grid
// shows neither, so its headers need a tighter floor than the data grid.
const HEADER_BASE       = 38;    // cell padding + pk dot + gaps (always present)
const FILTER_ICON_W     = 16;    // ⌕ filter icon + gap (table-data only)
const SORT_ICON_W       = 24;    // sort icon + gap (table-data only)

export default function Grid(props) {
  let scrollerRef;
  const [scrollTop, setScrollTop] = createSignal(0);
  const [scrollLeft, setScrollLeft] = createSignal(0);
  const [viewportH, setViewportH] = createSignal(400);

  // Column tooltip (full name + type, shown above the header while the mouse
  // is anywhere in the column). `hoverCol` is the hovered column NAME; header
  // cell elements are captured in headerEls so we can read their on-screen rect.
  const [hoverCol, setHoverCol] = createSignal(null);
  const headerEls = new Map();

  const colCount   = createMemo(() => props.columns?.length ?? 0);

  // Default width tracks the showTypes toggle.
  const defaultWidth = createMemo(
    () => (props.showTypes !== false ? DEFAULT_WIDTH_FULL : DEFAULT_WIDTH_COMPACT)
  );

  // Resolve final display order (column NAMES). Caller-provided takes priority;
  // otherwise we use the natural column order from `columns`.
  const orderNames = createMemo(() => {
    if (Array.isArray(props.displayOrder) && props.displayOrder.length > 0) {
      return props.displayOrder;
    }
    return (props.columns ?? []).map((c) => c.name);
  });

  // Map column name → { col, naturalIdx } for fast lookup when rendering rows.
  const colByName = createMemo(() => {
    const m = new Map();
    (props.columns ?? []).forEach((c, i) => m.set(c.name, { col: c, naturalIdx: i }));
    return m;
  });

  // Width for a given column name (fallback to default).
  const widthOf = (name) => {
    const w = props.widthByName?.[name];
    return typeof w === 'number' ? w : defaultWidth();
  };

  // Caller (Browser.jsx) always passes plain string[] — no defensive Set check.
  const pinnedSet   = createMemo(() => new Set(props.pinnedNames    ?? []));
  const excludedSet = createMemo(() => new Set(props.excludedFormat ?? []));
  const hiddenSet   = createMemo(() => new Set(props.hiddenNames    ?? []));

  // Feature gating by handler presence. The table-data grid (Browser) wires
  // filter / sort / reorder handlers; the query-result grid (QueryPanel) is a
  // static in-memory snapshot and wires none — so those affordances (filter
  // icon + clickable header, sort icon, drag-to-reorder) are hidden there.
  const canFilter   = () => typeof props.onHeaderClick === 'function';
  const canSort     = () => typeof props.onSortToggle  === 'function';
  const canReorder  = () => typeof props.onReorder     === 'function';

  // Sort lookup: column name → { dir, order } so headers can render the
  // direction arrow + multi-sort rank without re-scanning the array per cell.
  const sortIndex = createMemo(() => {
    const m = new Map();
    (props.sortKeys ?? []).forEach((s, i) => m.set(s.column, { dir: s.dir, order: i + 1 }));
    return m;
  });
  const sortCount = () => (props.sortKeys ?? []).length;

  // ── Spreadsheet-style edit model ──
  //
  // Single focused cell at a time. Focus implies editing — any editable cell
  // the user navigates to renders an <input>; the caret is auto-placed
  // (select-all) so typing replaces the value. Edits accumulate in
  // `bufferedRow` until the user leaves that row, at which point we fire
  // onRowEdit with everything that changed. PK cells ARE editable; the host
  // (Browser / QueryPanel) intercepts the on-row-edit fire to show an FK-
  // aware confirmation modal before the change is staged.
  const isPKColumn = (name) => {
    const it = colByName().get(name);
    return !!(it && it.col?.isPrimary);
  };

  const [focusedCell, setFocusedCell] = createSignal(null); // { rowIdx, colName }
  // In-cell caret editing: while true, Left/Right/Home/End move the text caret
  // (Ctrl+Left/Right jump by word) instead of navigating between cells. Enabled by
  // a MOUSE click into an editable cell (you clearly mean to edit text there) or by
  // F2; keyboard arrow-navigation INTO a cell leaves it off (text selected, arrows
  // move between cells). Reset whenever focus moves to a new cell.
  const [caretMode, setCaretMode] = createSignal(false);
  const [editText,    setEditText]    = createSignal('');
  // editTouched: the user actually changed the focused cell (typed, picked a
  // date / enum value, applied the value viewer). Merely moving THROUGH a cell
  // must never stage a change — a VARCHAR holding the text NULL used to become
  // SQL NULL, and every draft cell tabbed through used to INSERT ''.
  const [editTouched, setEditTouched] = createSignal(false);
  const editInput = (v) => { setEditText(v); setEditTouched(true); };
  // The focused cell edits in a <textarea> when its value has line breaks (an
  // <input> silently strips them on the first keystroke). Fixed at focus time.
  const [editMultiline, setEditMultiline] = createSignal(false);
  // bufferedRow: { rowIdx, changes: {colName: newValue}, row, key } — flushed
  // when the user navigates to a different DATA row. `row` is the row array the
  // edit was made on and `key` its getRowKey identity: when a sort / filter /
  // refresh re-orders props.rows, the buffer follows its ROW (not the display
  // index, which would now point at a different record). Draft (new) rows keep
  // their values in the drafts store instead and are only sent as INSERTs at Commit.
  const EMPTY_BUFFER = { rowIdx: null, changes: {}, row: null, key: null };
  let bufferedRow = EMPTY_BUFFER;
  const startBuffer = (rowIdx) => {
    bufferedRow = { rowIdx, changes: {}, row: props.rows?.[rowIdx] ?? null, key: props.getRowKey?.(rowIdx) ?? null };
  };

  // ── New-row drafts (insert) ───────────────────────────────────────────────
  // Client-only rows the user is composing, gated on an onRowInsert handler (so
  // grids without insert support are unchanged). Each draft gets a STABLE
  // synthetic row index = dataCount + array slot, so the existing focus / nav /
  // buffer machinery addresses it like any row; the array is append-only
  // (discarded drafts are flagged, never spliced) to keep those indices stable.
  // `after` is the row index this draft renders below (-1 = top). displayOrder
  // interleaves them among the data rows.
  const canInsert = () => typeof props.onRowInsert === 'function';
  const [drafts, setDrafts] = createSignal([]); // [{ values:{}, discarded, after }]
  const dataCount   = () => props.rows?.length ?? 0;
  const isDraftRow  = (rowIdx) => rowIdx >= dataCount();
  const draftSlot   = (rowIdx) => rowIdx - dataCount();
  const draftAt     = (rowIdx) => drafts()[draftSlot(rowIdx)];

  const displayOrder = createMemo(() => {
    const ds = drafts();
    const n  = dataCount();
    // Drafts only render while this grid is editable — the query-result grid is a
    // single instance reused across result tabs, so this keeps tab A's drafts
    // from showing when tab B (not in edit mode) is displayed.
    if (!props.editable || !ds.some((d) => d && !d.discarded)) {
      // Identity order, so behaviour is byte-for-byte the same as before drafts.
      const out = new Array(n);
      for (let i = 0; i < n; i++) out[i] = i;
      return out;
    }
    const order = [];
    const emitAfter = (anchor) => {
      ds.forEach((d, slot) => {
        if (d && !d.discarded && d.after === anchor) {
          const idx = n + slot;
          order.push(idx);
          emitAfter(idx);
        }
      });
    };
    emitAfter(-1);
    for (let i = 0; i < n; i++) { order.push(i); emitAfter(i); }
    return order;
  });
  const displayCount = () => displayOrder().length;
  const displayPosOf = (rowIdx) => {
    const ord = displayOrder();
    // Fast path: with no drafts the order is identity, so position === index
    // (avoids an O(n) indexOf per visible row on large grids).
    if (ord.length === dataCount()) return rowIdx < ord.length ? rowIdx : -1;
    return ord.indexOf(rowIdx);
  };

  const cellValue = (rowIdx, name) => {
    if (isDraftRow(rowIdx)) {
      const d = draftAt(rowIdx);
      if (!d) return null;
      return Object.prototype.hasOwnProperty.call(d.values, name) ? d.values[name] : null;
    }
    const item = colByName().get(name);
    const row  = props.rows?.[rowIdx];
    if (!item || !row) return null;
    return row[item.naturalIdx];
  };
  const cellAsText = editTextOf; // NULL → 'NULL'; the text 'NULL' → "'NULL'"
  const sameVal = (a, b) =>
    (a == null && b == null) ||
    (a != null && b != null && String(a) === String(b));
  // The value the buffered row's cell had when editing began — from the row it
  // was made on, so a re-ordered props.rows can't swap in another record's value.
  const originalValue = (rowIdx, name) => {
    if (bufferedRow.rowIdx === rowIdx && bufferedRow.row) {
      const it = colByName().get(name);
      return it ? bufferedRow.row[it.naturalIdx] : null;
    }
    return cellValue(rowIdx, name);
  };

  // ── Read-only cell selection + copy (query results) ──────────────────────
  // Active only when `selectable` and NOT editable (editing owns the click).
  // Coordinates are (rowIdx, displayPos) — display column position, not natural.
  const selectable = () => !!props.selectable && !props.editable;
  const [selAnchor, setSelAnchor] = createSignal(null); // { r, c }
  const [selFocus,  setSelFocus]  = createSignal(null);
  const [flashCell, setFlashCell] = createSignal(null);  // { r, name } — brief post-copy highlight
  let dragging = false, dragged = false, flashTimer = null;

  const selRect = createMemo(() => {
    const a = selAnchor(), f = selFocus();
    if (!a || !f) return null;
    return { r1: Math.min(a.r, f.r), r2: Math.max(a.r, f.r), c1: Math.min(a.c, f.c), c2: Math.max(a.c, f.c) };
  });
  const inSelection = (rowIdx, pos) => {
    const r = selRect();
    return !!r && rowIdx >= r.r1 && rowIdx <= r.r2 && pos >= r.c1 && pos <= r.c2;
  };
  // A new result set (rows/columns change identity) clears the selection.
  createEffect(() => { props.rows; props.columns; setSelAnchor(null); setSelFocus(null); });

  // Live selection statistics for the status footer (Excel-style): count of
  // non-empty cells + sum/avg/min/max over the numeric cells in the selection.
  // Recomputed reactively as the drag extends selFocus. Capped so a huge
  // selection (drag across thousands of loaded rows) can't stall the drag —
  // above the cap we still report the dimensions but skip the per-cell scan.
  const SEL_STATS_CAP = 200000;
  const selStats = createMemo(() => {
    if (!selectable()) return null;
    const r = selRect(); if (!r) return null;
    const rows = r.r2 - r.r1 + 1, cols = r.c2 - r.c1 + 1, cells = rows * cols;
    if (cells <= 1) return null; // a single cell isn't a "selection" worth summarizing
    if (cells > SEL_STATS_CAP) return { rows, cols, cells, capped: true };
    // Resolve each selected column's natural index + numeric-ness ONCE (C lookups,
    // not R×C). A selectable grid is never editable, so there are no draft rows —
    // we can read props.rows directly instead of going through cellValue per cell.
    const names = orderNames(), cbn = colByName();
    const colsMeta = [];
    for (let c = r.c1; c <= r.c2; c++) {
      const info = cbn.get(names[c]);
      colsMeta.push({ idx: info ? info.naturalIdx : -1, numeric: isNumeric(names[c]) });
    }
    const rowsArr = props.rows || [];
    let count = 0, numCount = 0, sum = 0, min = null, max = null;
    for (let row = r.r1; row <= r.r2; row++) {
      const rowData = rowsArr[row];
      if (!rowData) continue;
      for (const { idx, numeric } of colsMeta) {
        if (idx < 0) continue;
        const v = rowData[idx];
        if (v === null || v === undefined || v === '') continue;
        count++;
        if (numeric) {
          const num = Number(v);
          if (Number.isFinite(num)) { numCount++; sum += num; if (min === null || num < min) min = num; if (max === null || num > max) max = num; }
        }
      }
    }
    return { rows, cols, cells, count, numCount, sum, avg: numCount ? sum / numCount : null, min, max, capped: false };
  });

  const flash = (rowIdx, name) => {
    setFlashCell({ r: rowIdx, name });
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => setFlashCell(null), 450);
  };
  onCleanup(() => { if (flashTimer) clearTimeout(flashTimer); });

  // Shared copy + feedback: success toast on write, error toast on failure.
  const copyWithToast = async (text, successMsg) => {
    try { await copyText(text); toast.success(successMsg); }
    catch (e) { toast.error(e.message); }
  };

  const copyCellValue = async (rowIdx, name) => {
    const v = cellValue(rowIdx, name);
    try { await copyText(v == null ? '' : String(v)); flash(rowIdx, name); } // flash, not a toast
    catch (e) { toast.error(e.message); }
  };
  const copyRange = (withHeader = false) => {
    const r = selRect(); if (!r) return;
    const names = orderNames();
    const lines = [];
    if (withHeader) {
      const hdr = [];
      for (let c = r.c1; c <= r.c2; c++) hdr.push(escapeTSVField(names[c]));
      lines.push(hdr.join('\t'));
    }
    for (let row = r.r1; row <= r.r2; row++) {
      const cells = [];
      for (let c = r.c1; c <= r.c2; c++) cells.push(escapeTSVField(cellValue(row, names[c])));
      lines.push(cells.join('\t'));
    }
    copyWithToast(lines.join('\n'), `Copied ${r.r2 - r.r1 + 1}×${r.c2 - r.c1 + 1} cells${withHeader ? ' + header' : ''}`);
  };

  // Select an entire column (all loaded rows) — bound to a plain header click.
  // Index-based, so it covers off-screen virtualised rows too; focusing the
  // scroller lets Ctrl/Cmd+C copy it and the stats footer summarise it. Only
  // in a selectable (browse) grid — the editable grid owns the header click.
  const selectColumn = (pos) => {
    if (!selectable()) return;
    const n = props.rows?.length || 0;
    if (n === 0 || pos < 0) return;
    setSelAnchor({ r: 0, c: pos });
    setSelFocus({ r: n - 1, c: pos });
    scrollerRef?.focus?.();
  };

  // ── Foreign-key cell affordances (host-driven) ───────────────────────────
  // The host marks FK columns (isFkColumn), navigates to a parent row
  // (onFkNavigate), and lazily supplies parent-value suggestions for the edit
  // datalist (fetchFkOptions). All optional — a grid without them is unaffected.
  const isFk = (name) => !!props.isFkColumn?.(name);
  const gridUid = createUniqueId();
  const fkListId = (name) => `fk-${gridUid}-${String(name).replace(/[^A-Za-z0-9_-]/g, '_')}`;
  const [fkOpts, setFkOpts] = createSignal({}); // colName → string[] (parent values)
  const ensureFkOptions = (name) => {
    if (!props.fetchFkOptions || !isFk(name) || (name in fkOpts())) return;
    setFkOpts((m) => ({ ...m, [name]: [] })); // mark requested so we fetch once
    Promise.resolve(props.fetchFkOptions(name))
      .then((opts) => setFkOpts((m) => ({ ...m, [name]: Array.isArray(opts) ? opts : [] })))
      .catch(() => {});
  };
  // Ctrl/Cmd held → FK cells render as clickable links (Ctrl+click = open parent).
  const [ctrlHeld, setCtrlHeld] = createSignal(false);
  onMount(() => {
    const kd = (e) => { if (e.key === 'Control' || e.key === 'Meta') setCtrlHeld(true); };
    const ku = (e) => { if (e.key === 'Control' || e.key === 'Meta') setCtrlHeld(false); };
    const clear = () => setCtrlHeld(false);
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    window.addEventListener('blur', clear);
    onCleanup(() => {
      window.removeEventListener('keydown', kd);
      window.removeEventListener('keyup', ku);
      window.removeEventListener('blur', clear);
    });
  });

  // Ctrl/Cmd+A in an EDITABLE grid → select all rows (gutter selection), and
  // suppress the browser's "select all page text". Acts when focus is inside this
  // grid, or when a row selection is already active (e.g. after a gutter click that
  // left focus on the body). Skipped while editing a cell's text in caret mode —
  // there Ctrl+A selects the cell's own text natively. Capture phase so it beats
  // the default selection.
  onMount(() => {
    const onKey = (e) => {
      if (!props.editable || !props.getRowKey) return;
      if (!(e.ctrlKey || e.metaKey) || (e.key !== 'a' && e.key !== 'A')) return;
      const ae = document.activeElement;
      const inGrid = scrollerRef && scrollerRef.contains(ae);
      const rowSelActive = selectedRowSet().size > 0; // Delete-row button showing
      // Row-selection workflow → always select all rows. Otherwise, while actively
      // editing a cell's text (caret mode) leave Ctrl+A to select the cell's text.
      if (!rowSelActive) {
        if (inGrid && caretMode() && ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) return;
        if (!inGrid) return; // not this grid's context
      }
      e.preventDefault();
      selectAllRows();
    };
    window.addEventListener('keydown', onKey, true);
    onCleanup(() => window.removeEventListener('keydown', onKey, true));
  });

  const onCellMouseDown = (rowIdx, pos, e) => {
    if (!selectable() || e.button !== 0) return;
    if (e.target.closest && e.target.closest('.grid-cell-expand')) return; // let the ⤢ button work
    const name = orderNames()[pos];
    const mod = e.ctrlKey || e.metaKey;

    // Ctrl/Cmd+Shift+click → copy the clicked cell to the clipboard (it also
    // selects). Copying deliberately requires this combo so navigating/selecting
    // never writes to the clipboard by accident.
    if (mod && e.shiftKey) {
      e.preventDefault();
      setSelAnchor({ r: rowIdx, c: pos });
      setSelFocus({ r: rowIdx, c: pos });
      dragging = false; dragged = false;
      scrollerRef?.focus?.();
      copyCellValue(rowIdx, name);
      return;
    }
    // Ctrl/Cmd+click an FK cell with a value → jump to the referenced parent row
    // (the same action as the right-click "Go to <parent>" item).
    if (mod && !e.shiftKey && isFk(name) && cellValue(rowIdx, name) != null) {
      e.preventDefault();
      setSelAnchor({ r: rowIdx, c: pos });
      setSelFocus({ r: rowIdx, c: pos });
      dragging = false; dragged = false;
      scrollerRef?.focus?.();
      props.onFkNavigate?.(name, rowIdx);
      return;
    }
    // Shift+click → extend the selection from the anchor to this cell (a
    // rectangular multi-cell range), like Shift+arrow but with the mouse.
    if (e.shiftKey && !mod) {
      e.preventDefault(); // suppress native text selection
      if (selAnchor() == null) setSelAnchor({ r: rowIdx, c: pos });
      setSelFocus({ r: rowIdx, c: pos });
      dragging = false; dragged = true;
      scrollerRef?.focus?.();
      return;
    }
    // Plain click → select/focus the cell and start a drag-select. Suppress the
    // browser's native text selection: the cells carry `user-select: text` (so a
    // value stays copyable), but a cell-range drag must select CELLS, not the
    // text inside them (Excel-style). Copy never relies on native selection —
    // Ctrl/Cmd+C and the context menu both go through copyRange()/selRect().
    e.preventDefault();
    setSelAnchor({ r: rowIdx, c: pos });
    setSelFocus({ r: rowIdx, c: pos });
    dragging = true; dragged = false;
    scrollerRef?.focus?.();
    const up = () => {
      window.removeEventListener('mouseup', up);
      dragging = false;
    };
    window.addEventListener('mouseup', up);
  };
  const onSelectExtend = (rowIdx, pos) => {
    if (dragging) { dragged = true; setSelFocus({ r: rowIdx, c: pos }); }
  };

  // ── Editable-grid row selection (gutter click) ───────────────────────────
  // Click the row-number gutter in an editable grid to select a row. Standard
  // modifiers: Shift = range from the anchor (in display order), Ctrl/Cmd =
  // additive toggle, plain = single-row toggle.
  //
  // Storage is BY KEY (PK tuple) when props.getRowKey is provided — so a
  // local sort or filter doesn't shuffle the selection into the wrong rows.
  // Only rows whose keys disappear from the new view drop off. When no
  // getRowKey is wired, we fall back to raw-idx storage and clear on every
  // rows-identity change (the safe-but-lossy default).
  //
  // Drafts (unsaved new rows) are never selectable: they don't exist on the
  // server, so a DELETE by PK would be meaningless.
  const [selectedRowSet, setSelectedRowSet] = createSignal(new Set()); // Set<key|idx>
  const [rowSelAnchor,   setRowSelAnchor]   = createSignal(null);      // key|idx of last plain/ctrl click
  // keyOf(idx) returns the row's stable identity (key when getRowKey is
  // wired, otherwise the raw idx itself).
  const keyOf = (idx) => (props.getRowKey ? props.getRowKey(idx) : idx);
  const isRowSelected = (idx) => {
    const k = keyOf(idx);
    return k != null && selectedRowSet().has(k);
  };
  // Select every (non-draft) loaded row — Ctrl/Cmd+A in an editable grid.
  const selectAllRows = () => {
    if (!props.editable || !props.getRowKey) return;
    const n = (props.rows || []).length;
    const all = new Set();
    for (let i = 0; i < n; i++) {
      if (isDraftRow(i) || rowKeyLossy(i)) continue; // drafts / unrepresentable keys → not deletable
      const k = keyOf(i);
      if (k != null) all.add(k);
    }
    setSelectedRowSet(all);
  };

  // Reconcile + emit (key mode only). On every rows/set change, walk the
  // current rows to (a) prune keys whose rows are no longer present, and
  // (b) emit the current RAW INDICES of the survivors so the parent's delete
  // handler always sees fresh positions. Combined into one effect so the
  // parent never sees a stale-indices intermediate state during a flush.
  // When the parent doesn't wire getRowKey, gutter selection is disabled
  // entirely (see onGutterClick) — no fallback "selection by idx" semantics
  // because a new rows array can't promise the same rows live at the same
  // positions, and silently mis-targeting a DELETE is the worst outcome.
  createEffect(() => {
    const rows = props.rows || []; // tracks rows-identity
    const sel  = selectedRowSet(); // tracks set changes
    if (!props.getRowKey) {
      props.onRowSelectionChange?.([]);
      return;
    }
    const n = rows.length;
    const presentKeys = new Set();
    const presentIdxs = [];
    for (let i = 0; i < n; i++) {
      const k = props.getRowKey(i);
      if (k != null && sel.has(k)) {
        presentKeys.add(k);
        presentIdxs.push(i);
      }
    }
    // Prune dead keys; next pass is a fixed point (presentKeys === new sel).
    if (presentKeys.size !== sel.size) {
      setSelectedRowSet(presentKeys);
      return;
    }
    // Drop the anchor if its row is gone from the current view.
    const anchor = untrack(() => rowSelAnchor());
    if (anchor != null && !presentKeys.has(anchor)) {
      setRowSelAnchor(null);
    }
    props.onRowSelectionChange?.(presentIdxs);
  });

  // Editable→false unconditionally clears the selection (separate from the
  // reconcile so leaving edit mode doesn't depend on rows being touched).
  createEffect(() => {
    if (!props.editable) {
      setSelectedRowSet(new Set());
      setRowSelAnchor(null);
    }
  });

  const onGutterClick = (idx, e) => {
    if (!props.editable) return;
    if (!props.getRowKey) return; // no stable identity → no selection (see reconcile note)
    if (isDraftRow(idx)) return;  // unsaved draft — no PK to delete by
    if (rowKeyLossy(idx)) return; // its key can't be sent back exactly
    e.preventDefault();
    e.stopPropagation();
    const k = keyOf(idx);
    if (k == null) return; // can't select what we can't identify
    const sel = new Set(selectedRowSet());
    const anchor = rowSelAnchor();
    if (e.shiftKey && anchor != null) {
      // Range select in DISPLAY order — same visible rectangle the user
      // expects under any sort/filter. Locate both endpoints by KEY (since
      // raw idxs can shuffle between renders).
      const order = displayOrder();
      let anchorPos = -1;
      for (let i = 0; i < order.length; i++) {
        const di = order[i];
        if (isDraftRow(di)) continue;
        if (keyOf(di) === anchor) { anchorPos = i; break; }
      }
      const thisPos = order.indexOf(idx);
      if (anchorPos >= 0 && thisPos >= 0) {
        const [from, to] = anchorPos <= thisPos ? [anchorPos, thisPos] : [thisPos, anchorPos];
        for (let i = from; i <= to; i++) {
          const di = order[i];
          if (isDraftRow(di)) continue;
          const kk = keyOf(di);
          if (kk != null) sel.add(kk);
        }
      } else {
        // Stale anchor (scrolled out, filtered, etc.) — fall back to just this row.
        sel.add(k);
      }
      // Don't move the anchor on Shift+click — repeated Shift+click should
      // grow / shrink the range from the same origin.
    } else if (e.ctrlKey || e.metaKey) {
      // Additive toggle: flip this row in/out of the set; anchor moves here.
      if (sel.has(k)) sel.delete(k); else sel.add(k);
      setRowSelAnchor(k);
    } else {
      // Plain click: select this row alone, or clear if it was already the
      // only selected row (toggle behaviour).
      if (sel.size === 1 && sel.has(k)) {
        sel.clear();
        setRowSelAnchor(null);
      } else {
        sel.clear();
        sel.add(k);
        setRowSelAnchor(k);
      }
    }
    setSelectedRowSet(sel);
  };

  // ── Keyboard active-cell model (read-only selectable grid) ────────────────
  // selFocus IS the active cell; selAnchor the selection start. A selectable
  // grid is never editable, so there are no draft rows and (rowIdx === displayPos)
  // — coordinates are plain [0..rowCount) × [0..colCount). Arrows move the active
  // cell, Shift+arrows extend the range, Ctrl/Cmd+A selects all, Esc clears.
  const navRowCount = () => props.rows?.length || 0;
  const navColCount = () => orderNames().length; // display columns (selection coords index these)
  const moveActive = (r, c, extend) => {
    if (navRowCount() === 0 || navColCount() === 0) return;
    const cur = selFocus() || { r: 0, c: 0 };
    if (extend && !selAnchor()) setSelAnchor(cur);
    const nf = { r, c };
    setSelFocus(nf);
    if (!extend) setSelAnchor(nf);
    scrollerRef?.focus?.();
  };
  const clamp = (v, max) => Math.max(0, Math.min(max, v));
  const onGridKeyDown = (e) => {
    if (!selectable()) return;
    const mod = e.ctrlKey || e.metaKey;
    // Ctrl/Cmd+F — open the in-grid find bar.
    if (mod && (e.key === 'f' || e.key === 'F')) { e.preventDefault(); openFind(); return; }
    if (mod && (e.key === 'c' || e.key === 'C') && selRect()) { e.preventDefault(); copyRange(); return; }
    if (mod && (e.key === 'a' || e.key === 'A')) { // select all
      e.preventDefault();
      if (navRowCount() && navColCount()) { setSelAnchor({ r: 0, c: 0 }); setSelFocus({ r: navRowCount() - 1, c: navColCount() - 1 }); }
      return;
    }
    if (e.key === 'Escape') {
      if (findOpen()) { closeFind(); return; }
      if (selAnchor()) { e.preventDefault(); setSelAnchor(null); setSelFocus(null); }
      return;
    }
    // Space (no modifiers) on a focused cell → open the rich cell-value viewer.
    // Works on the read-only result grid; the editable grid uses focusCell+input
    // for editing so Space there would type into an input, not reach this.
    if (e.key === ' ' && !mod && !e.shiftKey && !e.altKey) {
      const f = selFocus();
      if (f) {
        const names = orderNames();
        const colName = names[f.c];
        if (colName) {
          e.preventDefault();
          const v = cellValue(displayOrder()[f.r], colName);
          openExpand(displayOrder()[f.r], colName, v, false);
        }
      }
      return;
    }
    // First nav keystroke with no active cell just establishes it at (0,0).
    if (!selFocus() && NAV_KEYS.has(e.key)) {
      if (navRowCount() && navColCount()) { e.preventDefault(); setSelAnchor({ r: 0, c: 0 }); setSelFocus({ r: 0, c: 0 }); }
      return;
    }
    const f = selFocus() || { r: 0, c: 0 };
    const pageRows = Math.max(1, Math.floor((scrollerRef?.clientHeight || ROW_HEIGHT * 10) / ROW_HEIGHT) - 1);
    const last = navRowCount() - 1, lastCol = navColCount() - 1;
    switch (e.key) {
      case 'ArrowUp':    e.preventDefault(); moveActive(clamp(f.r - 1, last), f.c, e.shiftKey); break;
      case 'ArrowDown':  e.preventDefault(); moveActive(clamp(f.r + 1, last), f.c, e.shiftKey); break;
      case 'ArrowLeft':  e.preventDefault(); moveActive(f.r, clamp(f.c - 1, lastCol), e.shiftKey); break;
      case 'ArrowRight': e.preventDefault(); moveActive(f.r, clamp(f.c + 1, lastCol), e.shiftKey); break;
      case 'Home':       e.preventDefault(); moveActive(e.ctrlKey ? 0 : f.r, 0, e.shiftKey); break;
      case 'End':        e.preventDefault(); moveActive(e.ctrlKey ? last : f.r, lastCol, e.shiftKey); break;
      case 'PageUp':     e.preventDefault(); moveActive(clamp(f.r - pageRows, last), f.c, e.shiftKey); break;
      case 'PageDown':   e.preventDefault(); moveActive(clamp(f.r + pageRows, last), f.c, e.shiftKey); break;
      default: /* ignore */
    }
  };

  // Scroll a given display row position into view in the virtualised scroller
  // (an off-screen row isn't in the DOM). The sticky header overlays the top
  // `headerH`px, so a row is only fully visible below it. Shared by edit-mode
  // focus + the selectable keyboard active cell.
  const ensureRowVisible = (pos) => {
    if (pos < 0 || !scrollerRef) return;
    const headerH = scrollerRef.querySelector('.grid-header')?.offsetHeight || ROW_HEIGHT;
    // The sticky header reserves the first `headerH`px of normal flow, so the
    // rows' spacer — and every row in it — actually starts at content-y headerH,
    // NOT 0. Include that offset or the down-check fires a row late and lands
    // short, leaving the focused cell hidden under the fold.
    const top = headerH + pos * ROW_HEIGHT, bottom = top + ROW_HEIGHT;
    if (top < scrollerRef.scrollTop + headerH) scrollerRef.scrollTop = Math.max(0, top - headerH);
    else if (bottom > scrollerRef.scrollTop + scrollerRef.clientHeight)
      scrollerRef.scrollTop = bottom - scrollerRef.clientHeight;
  };
  // Scroll a given display column position into view horizontally. The gutter +
  // any pinned columns are sticky-left (they overlay scrolled content), so a
  // non-pinned column is only visible to the RIGHT of that frozen region.
  const ensureColVisible = (c) => {
    if (c == null || c < 0 || !scrollerRef) return;
    const names = orderNames();
    if (c >= names.length || c < pinnedCount()) return; // pinned/gutter cols never scroll out
    const offs = liveLeftOffsets();
    const frozen = gutterWidth() + (pinnedCount() > 0 ? offs[pinnedCount()] : 0);
    const colLeft = gutterWidth() + offs[c];
    const colRight = colLeft + liveWidthOf(names[c]);
    if (colLeft < scrollerRef.scrollLeft + frozen) scrollerRef.scrollLeft = Math.max(0, colLeft - frozen);
    else if (colRight > scrollerRef.scrollLeft + scrollerRef.clientWidth)
      scrollerRef.scrollLeft = colRight - scrollerRef.clientWidth;
  };
  // Keyboard nav moves selFocus → keep its cell visible on BOTH axes (no drafts
  // in a selectable grid, so the row position is selFocus().r directly).
  createEffect(() => {
    if (!selectable()) return;
    const f = selFocus();
    if (f) { ensureRowVisible(f.r); ensureColVisible(f.c); }
  });

  // ── In-grid find (Ctrl+F) over the loaded rows ────────────────────────────
  const [findOpen, setFindOpen]   = createSignal(false);
  const [findText, setFindText]   = createSignal('');  // immediate input value
  const [findQuery, setFindQuery] = createSignal('');  // debounced search term
  const [findIdx, setFindIdx]     = createSignal(0);
  let findInputRef, findDebounce;
  // Debounce the scan: findMatches is O(rows × cols), so don't rescan on every
  // keystroke (matters at the 50k-row cap). The box (findText) updates instantly.
  const onFindInput = (e) => {
    const v = e.target.value;
    setFindText(v);
    clearTimeout(findDebounce);
    findDebounce = setTimeout(() => setFindQuery(v), 200);
  };
  onCleanup(() => clearTimeout(findDebounce));
  const openFind = () => { setFindOpen(true); setTimeout(() => findInputRef?.select?.(), 0); };
  const closeFind = () => { setFindOpen(false); scrollerRef?.focus?.(); };

  // All matching cells (substring, case-insensitive) over the displayed rows.
  // Column natural indices resolved once (no per-cell colByName lookup).
  const findMatches = createMemo(() => {
    const q = findOpen() ? findQuery().trim().toLowerCase() : '';
    if (!q) return [];
    const names = orderNames(), cbn = colByName();
    const idxs = names.map((n) => cbn.get(n)?.naturalIdx ?? -1);
    const rows = props.rows || [];
    const out = [];
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      if (!row) continue;
      for (let c = 0; c < idxs.length; c++) {
        const ci = idxs[c];
        if (ci < 0) continue;
        const v = row[ci];
        if (v != null && String(v).toLowerCase().includes(q)) out.push({ r, c });
      }
    }
    return out;
  });
  const findSet = createMemo(() => new Set(findMatches().map((m) => m.r + ':' + m.c)));
  const currentFindKey = createMemo(() => { const cf = findMatches()[findIdx()]; return cf ? cf.r + ':' + cf.c : null; });
  // When the match set changes, reset to the first and jump to it.
  createEffect(() => {
    const m = findMatches();
    setFindIdx(0);
    if (m.length) { setSelAnchor(m[0]); setSelFocus(m[0]); }
  });
  const gotoMatch = (delta) => {
    const m = findMatches();
    if (!m.length) return;
    const i = (findIdx() + delta + m.length) % m.length;
    setFindIdx(i);
    setSelAnchor(m[i]); setSelFocus(m[i]);
  };
  const onFindKey = (e) => {
    if (e.key === 'Enter')  { e.preventDefault(); gotoMatch(e.shiftKey ? -1 : +1); }
    if (e.key === 'Escape') { e.preventDefault(); closeFind(); }
  };
  const isActiveCell = (r, c) => { const f = selFocus(); return selectable() && f && f.r === r && f.c === c; };
  // Find-highlight class for a cell, given its precomputed "r:c" key.
  const cellFindClass = (key) => (findSet().has(key)
    ? (currentFindKey() === key ? ' grid-cell-find grid-cell-find-current' : ' grid-cell-find')
    : '');

  // ── Copy-column / copy-row / column-stats menu (query results) ────────────
  const [menu,  setMenu]  = createSignal(null); // { x, y, items }
  const [stats, setStats] = createSignal(null); // computed stats for the modal

  const copyColumn = (name) => {
    const info = colByName().get(name); if (!info) return;
    const idx = info.naturalIdx;
    const vals = (props.rows ?? []).map((row) => escapeTSVField(row[idx]));
    copyWithToast(vals.join('\n'), `Copied column “${name}” (${vals.length} rows)`);
  };
  const copyRow = (rowIdx, withHeader = false) => {
    const names = orderNames();
    const cells = names.map((n) => escapeTSVField(cellValue(rowIdx, n))).join('\t');
    const out = withHeader
      ? `${names.map((n) => escapeTSVField(n)).join('\t')}\n${cells}`
      : cells;
    copyWithToast(out, withHeader ? 'Copied row + header' : 'Copied row');
  };

  // Format-copies (JSON / Markdown / INSERT) operate on the ROW SPAN of the
  // current selection (every column of the selected rows) — falling back to the
  // single right-clicked row. This is the HeidiSQL "copy rows as INSERT" gesture
  // and the natural shape for JSON/Markdown too.
  const rowSpanData = (rowIdx) => {
    const r = selRect();
    const r1 = r ? r.r1 : rowIdx, r2 = r ? r.r2 : rowIdx;
    const names = orderNames();
    const matrix = [];
    for (let row = r1; row <= r2; row++) matrix.push(names.map((n) => cellValue(row, n)));
    return { colNames: names, matrix, numericByCol: names.map((n) => isNumeric(n)), count: r2 - r1 + 1 };
  };
  const copyAsJson = (rowIdx) => {
    const d = rowSpanData(rowIdx);
    copyWithToast(buildJson(d.colNames, d.matrix), `Copied ${d.count} row(s) as JSON`);
  };
  const copyAsMarkdown = (rowIdx) => {
    const d = rowSpanData(rowIdx);
    copyWithToast(buildMarkdown(d.colNames, d.matrix), `Copied ${d.count} row(s) as Markdown`);
  };
  const copyAsInsert = (rowIdx) => {
    const d = rowSpanData(rowIdx);
    const table = props.insertTable || 'tablename'; // query results have no single table — placeholder
    copyWithToast(buildInserts(table, d.colNames, d.matrix, d.numericByCol), `Copied ${d.count} row(s) as INSERT`);
  };

  // Client-side column stats over the loaded rows (the result is already capped).
  const openStats = (name) => {
    const info = colByName().get(name); if (!info) return;
    const idx = info.naturalIdx;
    const rows = props.rows ?? [];
    const numeric = isNumeric(name);
    const distinct = new Set();
    let nonNull = 0, nulls = 0, sum = 0, numCount = 0;
    let min = null, max = null, sMin = null, sMax = null;
    for (const row of rows) {
      const v = row[idx];
      if (v === null || v === undefined) { nulls++; continue; }
      nonNull++;
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      distinct.add(s);
      if (sMin === null || s < sMin) sMin = s;
      if (sMax === null || s > sMax) sMax = s;
      if (numeric) {
        const num = Number(v);
        if (Number.isFinite(num)) { numCount++; sum += num; if (min === null || num < min) min = num; if (max === null || num > max) max = num; }
      }
    }
    setStats({
      name, numeric, total: rows.length, nonNull, nulls, distinct: distinct.size,
      min, max, sum, avg: numCount ? sum / numCount : null, sMin, sMax,
    });
  };

  // Per-column actions shared by the header menu and the bottom of the cell menu.
  const columnMenuItems = (name) => [
    { label: 'Copy column', icon: '▤', onClick: () => copyColumn(name) },
    { label: 'Column stats…', icon: 'Σ', onClick: () => openStats(name) },
  ];
  // Optional parent-supplied items appended (after a divider) to the internal
  // copy menus, so a selectable grid keeps its owner's column/cell actions
  // (e.g. the data grid's Pin / Hide / Move / Edit-table). Returns [] if none.
  const withExtra = (items, extra) =>
    (extra && extra.length) ? [...items, { divider: true }, ...extra] : items;

  // Header right-click → internal menu (selectable grids) or the parent's menu.
  const onHeaderMenu = (col, e) => {
    if (!selectable()) { props.onHeaderContextMenu?.(col, e); return; }
    e.preventDefault();
    const items = withExtra(columnMenuItems(col.name), props.extraHeaderItems?.(col));
    setMenu({ x: e.clientX, y: e.clientY, items });
  };
  // Cell right-click → internal copy/stats menu (selectable) or the parent's menu.
  const onCellMenu = (col, rowIdx, pos, e) => {
    if (!selectable()) { props.onCellContextMenu?.(col, e); return; }
    e.preventDefault();
    const name = col.name;
    if (!inSelection(rowIdx, pos)) { setSelAnchor({ r: rowIdx, c: pos }); setSelFocus({ r: rowIdx, c: pos }); }
    const r = selRect();
    const hasRange = r && (r.r1 !== r.r2 || r.c1 !== r.c2);
    const cellLabel = `${name}@row ${rowIdx + 1}`;
    const items = withExtra([
      // Rich value viewer — first item so it's the easiest to hit.
      { label: 'View value…', icon: '🔍', onClick: () => openExpand(rowIdx, name, cellValue(rowIdx, name), false) },
      { label: 'Pick for diff', icon: '✚',
        onClick: () => { const v = cellValue(rowIdx, name); setPickedCell({ value: v == null ? '' : String(v), label: cellLabel }); } },
      ...(pickedCell() ? [{
        label: `Diff vs picked (${pickedCell().label || 'cell'})`, icon: 'Δ',
        onClick: () => openExpand(rowIdx, name, cellValue(rowIdx, name), false),
        // The viewer auto-shows the Diff button when pickedCell() is set; user
        // clicks "Δ Diff vs picked" inside the modal. Could open in diff mode
        // directly in a later iteration.
      }] : []),
      { divider: true },
      { label: 'Copy cell', icon: '⧉', onClick: () => copyCellValue(rowIdx, name) },
      ...(hasRange ? [
        { label: 'Copy selection (TSV)', icon: '▦', onClick: () => copyRange(false) },
        { label: 'Copy selection + header', icon: '▦', onClick: () => copyRange(true) },
      ] : []),
      { label: 'Copy row (TSV)', icon: '☰', onClick: () => copyRow(rowIdx, false) },
      { label: 'Copy row + header', icon: '☰', onClick: () => copyRow(rowIdx, true) },
      { divider: true },
      // Format-copies of the selected rows (or this row): JSON / Markdown / INSERT.
      { label: hasRange ? 'Copy rows as JSON' : 'Copy row as JSON', icon: '{}', onClick: () => copyAsJson(rowIdx) },
      { label: hasRange ? 'Copy rows as Markdown' : 'Copy row as Markdown', icon: '▤', onClick: () => copyAsMarkdown(rowIdx) },
      { label: hasRange ? 'Copy rows as INSERT' : 'Copy row as INSERT', icon: '⎀', onClick: () => copyAsInsert(rowIdx) },
      { divider: true },
      ...columnMenuItems(name),
    ], props.extraCellItems?.(col, rowIdx));
    setMenu({ x: e.clientX, y: e.clientY, items });
  };
  const fmtNum = (n) => (n === null ? '—' : formatNumberWithSep(Number.isInteger(n) ? n : Number(n.toFixed(4))));

  // Persist whatever is in editText into the row buffer (or remove the entry
  // if the value matches the original — avoids spurious SET clauses).
  const persistCurrentEdit = () => {
    const f = focusedCell();
    if (!f || !editTouched()) return; // moving through a cell changes nothing
    const next = parseEditText(editText());
    // Draft (new) row → store the value directly on the draft (no original to
    // diff against; the draft IS the pending INSERT). An empty value means "not
    // set" — the column is left to its DEFAULT instead of INSERTing '' (which
    // strict mode rejects for numbers / dates and non-strict turns into 0).
    if (isDraftRow(f.rowIdx)) {
      if (!draftCellEditable(f.colName)) return;
      const slot = draftSlot(f.rowIdx);
      setDrafts((arr) => arr.map((d, i) => {
        if (i !== slot) return d;
        const { [f.colName]: _old, ...values } = d.values;
        return { ...d, values: next === '' ? values : { ...values, [f.colName]: next } };
      }));
      return;
    }
    if (bufferedRow.rowIdx !== f.rowIdx) startBuffer(f.rowIdx);
    bufferedRow = mergeLiveEdit(bufferedRow, f.colName);
  };

  // The buffer with the focused cell's typed value merged in — compared with
  // the value the cell had in the row the buffer was made on (b.row), and
  // dropped again when it's back to that value.
  const mergeLiveEdit = (b, colName) => {
    if (!editTouched()) return b;
    const it = colByName().get(colName);
    const original = it && b.row ? b.row[it.naturalIdx] : null;
    const parsed = parseEditText(editText());
    // A <textarea> reports CRLF as LF — don't let that rewrite every line break.
    const value = typeof parsed === 'string' ? restoreLineEndings(parsed, original) : parsed;
    const { [colName]: _drop, ...rest } = b.changes;
    return { ...b, changes: sameVal(original, value) ? rest : { ...rest, [colName]: value } };
  };

  // Flush the row buffer via onRowEdit. Returns a promise the parent can await.
  // ── Staged-edit (dirty cell) tracking ─────────────────────────────────────
  // Cells edited but not yet transaction-committed get a marker + feed the
  // host's "N changes" bar. Keyed by PK + column so a marker survives re-sort /
  // filter (the rowIdx is just a display position). Recorded when a row's edits
  // flush; the parent clears the whole set on Commit/Rollback (registerDirtyClearer).
  const [dirty, setDirty] = createSignal(new Set());
  const pkCols = createMemo(() => (props.columns || []).filter((c) => c.isPrimary).map((c) => c.name));
  // A row's staged-edit key, from the row array itself (so it can be computed
  // for a row no longer in props.rows).
  const dirtyKeyOfRow = (row, rowIdx) => {
    const pks = pkCols();
    if (!pks.length || !row) return `i${rowIdx}`; // no PK → fall back to display index
    return pks.map((n) => String(row[colByName().get(n)?.naturalIdx ?? -1])).join('|');
  };
  const rowDirtyKey = (rowIdx) => dirtyKeyOfRow(props.rows?.[rowIdx], rowIdx);
  createEffect(() => props.onDirtyChange?.(dirty().size));

  const flushBufferedRow = async () => {
    persistCurrentEdit();
    const b = bufferedRow;
    bufferedRow = EMPTY_BUFFER;
    props.onEditPreview?.(null, {}); // row is being sent → clear the live preview
    await sendBuffered(b);
  };

  // Send one row's buffered edits to the host (onRowEdit) and keep the
  // staged-edit markers in step with the outcome. (The buffer only ever holds a
  // DATA row — drafts keep their values in the drafts store.)
  const sendBuffered = async (b) => {
    if (b.rowIdx === null) return;
    if (Object.keys(b.changes).length === 0) return;
    // Mark the changed cells dirty. Done before the await so the markers
    // appear immediately.
    const rk = dirtyKeyOfRow(b.row, b.rowIdx);
    setDirty((s) => { const n = new Set(s); for (const c of Object.keys(b.changes)) n.add(`${rk}::${c}`); return n; });
    // The host may return `{ cancelled: true }` to walk back the edit (e.g.
    // PK-rename confirmation dialog cancelled, or the write failed). Drop the
    // dirty markers we just added — cellValue already reads the original row,
    // so the displayed value snaps back naturally once bufferedRow was cleared.
    const result = await props.onRowEdit?.(b.rowIdx, b.changes, { row: b.row });
    if (result && result.cancelled) {
      setDirty((s) => {
        const n = new Set(s);
        for (const c of Object.keys(b.changes)) n.delete(`${rk}::${c}`);
        return n;
      });
    }
  };

  // ── Keep edits on THEIR rows when props.rows changes ─────────────────────
  // A sort / filter / optimistic update / refresh hands the grid a re-ordered
  // rows array while a row edit is buffered (and maybe a cell focused). Display
  // indices now point at other records, so the buffer, the focus and the
  // drafts' anchors are re-pointed to the same ROW (by identity, else by
  // getRowKey). A buffered row that left the view is sent now — exactly as
  // moving off it would — keyed by the row it was made on. Runs before render
  // (createComputed), so no frame shows an edit on the wrong record.
  let lastRows = props.rows || [];
  createComputed(() => {
    const rows = props.rows || [];
    untrack(() => {
      const oldRows = lastRows;
      if (rows === oldRows) return;
      lastRows = rows;
      const oldN = oldRows.length, newN = rows.length;
      const find = (row, key) => {
        if (row) { const i = rows.indexOf(row); if (i >= 0) return i; }
        if (key != null && props.getRowKey) {
          for (let i = 0; i < newN; i++) if (props.getRowKey(i) === key) return i;
        }
        return -1;
      };
      const remap = (idx) => { // an old display index (data or draft) → the new one
        if (idx == null || idx < 0) return idx;
        if (idx >= oldN) return newN + (idx - oldN); // a draft keeps its slot
        const i = find(oldRows[idx], null);
        return i >= 0 ? i : newN - 1;
      };
      if (drafts().some((d) => d && !d.discarded && d.after >= 0)) {
        setDrafts((arr) => arr.map((d) => (d && d.after >= 0 ? { ...d, after: remap(d.after) } : d)));
      }
      const f = focusedCell();
      const b = bufferedRow;
      if (b.rowIdx !== null && b.rowIdx < oldN) {
        const ni = find(b.row, b.key);
        if (ni >= 0) {
          bufferedRow = { ...b, rowIdx: ni };
          if (f && f.rowIdx === b.rowIdx) setFocusedCell({ ...f, rowIdx: ni });
        } else {
          // (Merged from the buffer's own row snapshot: f.rowIdx is an OLD
          // index and could now look like a draft's.)
          const pending = f && f.rowIdx === b.rowIdx ? mergeLiveEdit(b, f.colName) : b;
          if (f && f.rowIdx === b.rowIdx) setFocusedCell(null);
          bufferedRow = EMPTY_BUFFER;
          queueMicrotask(() => { props.onEditPreview?.(null, {}); sendBuffered(pending); });
        }
      } else if (f && f.rowIdx >= oldN) {
        setFocusedCell({ ...f, rowIdx: newN + (f.rowIdx - oldN) }); // a focused draft
      } else if (f && f.rowIdx >= newN) {
        setFocusedCell(null);
      }
    });
  });

  // Live "would-run" preview of the focused row's edits: the buffered changes
  // merged with the focused cell's in-progress (not-yet-persisted) value. Fires
  // on every keystroke / column move so the parent can render the UPDATE before
  // it's sent — feedback that matters when a filtered result has a single row
  // (no other row to move to, so onRowEdit wouldn't fire until Commit).
  const emitPreview = () => {
    if (!props.onEditPreview) return;
    const f = focusedCell();
    const baseRow = f ? f.rowIdx : bufferedRow.rowIdx;
    if (baseRow === null || baseRow === undefined) { props.onEditPreview(null, {}, 'update'); return; }
    // Draft row → preview an INSERT of its set columns (merged with the live cell).
    if (isDraftRow(baseRow)) {
      const d = draftAt(baseRow);
      const changes = { ...(d ? d.values : {}) };
      if (f && f.rowIdx === baseRow && editTouched() && draftCellEditable(f.colName)) {
        const next = parseEditText(editText());
        if (next === '') delete changes[f.colName]; else changes[f.colName] = next;
      }
      props.onEditPreview(baseRow, changes, 'insert');
      return;
    }
    const changes = { ...(bufferedRow.rowIdx === baseRow ? bufferedRow.changes : {}) };
    if (f && f.rowIdx === baseRow && editTouched() && editableCol(f.colName)) {
      const original = originalValue(f.rowIdx, f.colName);
      const parsed = parseEditText(editText());
      const next = typeof parsed === 'string' ? restoreLineEndings(parsed, original) : parsed;
      if (sameVal(original, next)) delete changes[f.colName];
      else changes[f.colName] = next;
    }
    props.onEditPreview(baseRow, changes, 'update', { row: bufferedRow.rowIdx === baseRow ? bufferedRow.row : props.rows?.[baseRow] });
  };

  // Move focus to (rowIdx, colName). If the row differs from the current
  // buffer's row, flush first so the previous row's edits hit the server
  // before the user starts editing a new one.
  const focusCell = async (rowIdx, colName) => {
    if (!props.editable) return;
    const f = focusedCell();
    if (f && f.rowIdx === rowIdx) {
      // Same row, different column → persist editText into the buffer / draft.
      persistCurrentEdit();
    } else {
      // Leaving the previous row. A DATA row flushes its UPDATE now; a DRAFT row
      // just saves into the draft (it's sent as an INSERT only at Commit). An
      // EMPTY draft is discarded on row-leave so an untouched Append doesn't
      // accumulate ghost rows.
      if (f) {
        if (isDraftRow(f.rowIdx)) {
          persistCurrentEdit();
          const slot = draftSlot(f.rowIdx);
          if (draftIsEmpty(slot)) markDraftDiscarded(slot);
        }
        else await flushBufferedRow();
      }
      if (!isDraftRow(rowIdx)) startBuffer(rowIdx);
    }
    // Pre-fill editText.
    let display;
    if (isDraftRow(rowIdx)) {
      const d = draftAt(rowIdx);
      const has = d && Object.prototype.hasOwnProperty.call(d.values, colName);
      const v = has ? d.values[colName] : undefined;
      display = v === undefined ? '' : editTextOf(v);
    } else {
      const buffered = bufferedRow.changes[colName];
      display = editTextOf(buffered !== undefined ? buffered : cellValue(rowIdx, colName));
    }
    setEditText(display);
    setEditTouched(false);
    setEditMultiline(/[\r\n]/.test(display));
    setFocusedCell({ rowIdx, colName });
    setCaretMode(false); // a freshly-focused cell starts in navigation mode (text selected)
    emitPreview();
  };

  // ── New-row draft creation / flush ────────────────────────────────────────
  const firstDraftColumn = () => orderNames().find(draftCellEditable) || orderNames()[0];

  // A draft is "empty" when no insertable column holds a meaningful value —
  // matching the commit-flush rule that treats '' / null as "missing". The
  // merge with the live editText keeps an in-flight character from counting as
  // empty (so a draft you've started typing into is protected).
  const draftIsEmpty = (slot, mergeLive) => {
    const d = drafts()[slot];
    if (!d || d.discarded) return false;
    const values = mergeLive ? { ...d.values, ...mergeLive } : d.values;
    const eff = draftInsertValues({ values });
    return !Object.values(eff).some((v) => v !== null && v !== undefined && String(v) !== '');
  };

  // Whether the focused cell sits on an empty draft (live edit merged in).
  const focusedDraftIsEmpty = (f) => {
    if (!f || !isDraftRow(f.rowIdx)) return false;
    const live = (f.colName && editTouched() && draftCellEditable(f.colName))
      ? { [f.colName]: parseEditText(editText()) }
      : null;
    return draftIsEmpty(draftSlot(f.rowIdx), live);
  };

  // Create a draft row anchored after `after` (-1 = top) and focus it. Persists
  // / flushes whatever was being edited first. If the user is currently sitting
  // on an EMPTY draft, that one is discarded first — so Append/Insert/ArrowDown
  // can't pile up untouched rows. Done first so the next focus lands on the new
  // draft cleanly (focusCell handles persisting the old focused cell's value).
  const spawnDraft = async (after, focusCol) => {
    if (!canInsert()) return;
    const f = focusedCell();
    if (focusedDraftIsEmpty(f)) markDraftDiscarded(draftSlot(f.rowIdx));
    const slot = drafts().length;
    setDrafts((arr) => [...arr, { values: {}, discarded: false, after }]);
    await focusCell(dataCount() + slot, focusCol || firstDraftColumn());
  };
  // INSERT key → a new row directly ABOVE the focused row.
  const insertDraftAboveFocused = () => {
    const f = focusedCell();
    const pos = f ? displayPosOf(f.rowIdx) : displayCount();
    spawnDraft(pos > 0 ? displayOrder()[pos - 1] : -1, f?.colName);
  };
  // Append → a new row at the very bottom (arrow-down gesture + toolbar button).
  const appendDraft = () => {
    const ord = displayOrder();
    spawnDraft(ord.length ? ord[ord.length - 1] : -1);
  };

  const markDraftDiscarded = (slot) =>
    setDrafts((arr) => arr.map((d, i) => (i === slot ? { ...d, discarded: true } : d)));
  const clearDrafts = () => setDrafts([]);

  // Esc on a draft row discards the whole pending row (it's never been sent —
  // drafts only go out at Commit). Silent when empty; a confirm dialog guards a
  // draft you've typed into. `discardConfirm` = { slot, el } while the dialog is
  // open (el = the input to refocus if the user keeps editing).
  const [discardConfirm, setDiscardConfirm] = createSignal(null);
  const discardDraft = (slot) => {
    markDraftDiscarded(slot);
    setDiscardConfirm(null);
    setFocusedCell(null); // the row is gone — nothing to keep focused
    emitPreview();
  };
  const cancelDiscard = () => {
    const dc = discardConfirm();
    setDiscardConfirm(null);
    if (dc?.el) setTimeout(() => dc.el.focus(), 0); // back to the cell, keep editing
  };

  // The columns to actually INSERT for a draft: the ones the user set, minus
  // auto-filled ones. An empty value means "not set" (the column's DEFAULT
  // applies) — never INSERT '' for a column merely tabbed through.
  const draftInsertValues = (d) => {
    const out = {};
    for (const [k, v] of Object.entries(d.values)) {
      if (!insertAutoFilled(k) && v !== '') out[k] = v;
    }
    return out;
  };
  const requiredMissing = (vals) => orderNames().filter((n) =>
    requiredForInsert(n) &&
    !(Object.prototype.hasOwnProperty.call(vals, n) && vals[n] !== null && String(vals[n]) !== ''));

  // Called by the parent before COMMIT: send each non-empty draft as an INSERT.
  // Untouched drafts are dropped; drafts missing a required field abort the
  // batch (returned in `validation`); a per-row backend verdict drives whether a
  // draft is consumed ('ok'), kept for retry ('skipped'), or the whole tx was
  // rolled back ('aborted' → stop).
  const flushDrafts = async () => {
    const f = focusedCell();
    if (f && isDraftRow(f.rowIdx)) persistCurrentEdit();
    const pending = [];
    const validation = [];
    drafts().forEach((d, slot) => {
      if (!d || d.discarded) return;
      const vals = draftInsertValues(d);
      if (Object.keys(vals).length === 0) { markDraftDiscarded(slot); return; }
      const missing = requiredMissing(vals);
      if (missing.length) { validation.push({ missing }); return; }
      pending.push({ slot, vals });
    });
    if (validation.length) return { validation, aborted: false };
    let aborted = false;
    for (const p of pending) {
      const verdict = await props.onRowInsert(p.vals); // 'ok' | 'skipped' | 'aborted'
      if (verdict === 'aborted') { aborted = true; break; }
      if (verdict === 'ok') markDraftDiscarded(p.slot);
    }
    return { validation: [], aborted };
  };

  // Emit EVERY pending draft row (each with the focused draft's in-progress cell
  // merged in) so the parent can list all pending INSERTs in its log — not just
  // the focused one. Mirrors flushDrafts' value selection (auto-filled columns
  // dropped, empty drafts skipped). Fires on draft add/discard/edit + typing.
  createEffect(() => {
    if (!props.onDraftsChange) return;
    const ds = drafts();
    const f = focusedCell();
    const live = editText();
    const touched = editTouched();
    const out = [];
    ds.forEach((d, slot) => {
      if (!d || d.discarded) return;
      const idx = dataCount() + slot;
      const merged = { ...d.values };
      if (f && f.rowIdx === idx && touched && draftCellEditable(f.colName)) {
        merged[f.colName] = parseEditText(live);
      }
      const vals = draftInsertValues({ values: merged });
      if (Object.keys(vals).length === 0) return; // nothing typed yet → no line
      out.push({ key: idx, values: vals });
    });
    props.onDraftsChange(out);
  });

  // Parent (Browser.jsx) calls flush before COMMIT/ROLLBACK/exit so any
  // unsaved row makes it to the server first.
  onMount(() => {
    props.registerFlusher?.(async () => { await flushBufferedRow(); });
    props.registerAutoFit?.(computeAutoFitWidths);
    props.registerInsertFlusher?.(flushDrafts);
    props.registerRowInserter?.(appendDraft);
    props.registerDraftClearer?.(clearDrafts);
    props.registerDirtyClearer?.(() => setDirty(new Set()));
    // Parent calls this after COMMIT/ROLLBACK/refetch so the "N selected"
    // toolbar doesn't stick around showing stale row indices.
    props.registerRowSelectionClearer?.(() => {
      setSelectedRowSet(new Set());
      setRowSelAnchor(null);
    });
  });
  // Leaving edit mode entirely clears the staged-edit markers (Commit/Rollback
  // while still editing clear via the registered clearer instead).
  createEffect(() => { if (!props.editable) setDirty(new Set()); });

  // ── Long-text expand modal ──
  const [expanded, setExpanded]   = createSignal(null); // { rowIdx, name, editable }
  const [expandText, setExpandText] = createSignal('');
  const openExpand = (rowIdx, name, value, editable) => {
    setExpandText(value == null ? '' : String(value));
    setExpanded({ rowIdx, name, editable });
  };
  const saveExpand = async () => {
    const ex = expanded();
    if (!ex?.editable) { setExpanded(null); return; }
    await focusCell(ex.rowIdx, ex.name); // set buffer row + focus this cell
    // The viewer edits the VALUE (not the cell-editor text form), so a typed
    // NULL here is the text, and only an actual change is staged.
    editInput(editTextOf(expandText()));
    persistCurrentEdit();                // write the new value into the row buffer
    setFocusedCell(null);                // re-render unfocused → shows the buffered preview
    setExpanded(null);
  };

  // Keyboard navigation while editing. Movement is over the DISPLAY order, so
  // interleaved draft (new) rows are traversed like any row.
  //   ArrowUp/Down — move row
  //   ArrowLeft/Right — move column (caret within the cell while in F2 caret mode)
  //   Home / End — first / last column of the row (caret line-start/end in F2 mode)
  //   Ctrl+Home / Ctrl+End — first / last cell of the whole grid
  //   PageUp / PageDown — move a viewport of rows, same column
  //   Tab / Shift+Tab — like horizontal arrows, wrap rows at the edges
  //   Enter — like ArrowDown (spreadsheet convention)
  //   F2 — toggle in-cell caret editing (Left/Right/Home/End move the caret)
  //   ArrowDown / Enter on the last row — append a new draft row (if insertable),
  //     but a no-op when already on an empty draft (the EOF append slot)
  //   Insert — add a new draft row directly above the focused row
  //   Escape — on a DRAFT row: discard the whole pending new row (silently if
  //     empty, else with a confirm dialog). On a data row: revert this cell's
  //     edit (keep the rest of the row buffered), unfocus.
  const onCellInputKey = (e) => {
    const order = orderNames();
    const f = focusedCell();
    if (!f) return;
    const colIdx = order.indexOf(f.colName);
    const ord = () => displayOrder();
    const lastCol = () => order.length - 1;
    const lastPos = () => ord().length - 1;
    const focusAt = (pos, col) => { e.preventDefault(); if (pos >= 0 && pos <= lastPos()) focusCell(ord()[pos], col); };

    const moveTo = (drow, dcol) => {
      e.preventDefault();
      const curPos = displayPosOf(f.rowIdx);
      let newCol = colIdx + dcol;
      let posDelta = drow;
      if (newCol < 0)             { posDelta -= 1; newCol = order.length - 1; }
      if (newCol >= order.length) { posDelta += 1; newCol = 0; }
      const newPos = curPos + posDelta;
      if (newPos < 0) return;
      if (newPos > lastPos()) {
        // Past the last row: a straight-down move (ArrowDown/Enter) appends a
        // new draft — UNLESS we're already sitting on an empty draft (the EOF
        // append slot). Appending another empty row there is pointless and would
        // churn/lose the caret, so it's a no-op (the caret stays put). A
        // mid-table INSERT draft never reaches here (it has rows below it), so
        // ArrowDown still moves down out of it normally.
        if (drow > 0 && dcol === 0 && canInsert() && !focusedDraftIsEmpty(f)) appendDraft();
        return;
      }
      focusCell(ord()[newPos], order[newCol]);
    };

    // F2 toggles in-cell caret editing (text inputs only). Collapsing the
    // selection to the end gives a visible cue (caret vs full-text highlight).
    const tag = e.currentTarget.tagName;
    const textual = tag === 'INPUT' || tag === 'TEXTAREA';
    if (e.key === 'F2') {
      if (!textual) return;
      e.preventDefault();
      const on = !caretMode();
      setCaretMode(on);
      if (on) { const n = e.currentTarget.value.length; e.currentTarget.setSelectionRange?.(n, n); }
      else e.currentTarget.select?.();
      return;
    }
    const caret = caretMode() && textual;
    const multiline = tag === 'TEXTAREA';
    const pageRows = Math.max(1, Math.floor((scrollerRef?.clientHeight || ROW_HEIGHT * 10) / ROW_HEIGHT) - 1);

    switch (e.key) {
      case 'ArrowLeft':  if (caret) return; moveTo(0, -1); break; // caret: native left
      case 'ArrowRight': if (caret) return; moveTo(0, +1); break; // caret: native right
      case 'ArrowUp':    if (caret && multiline) return; moveTo(-1, 0); break; // caret: native line up
      case 'ArrowDown':  if (caret && multiline) return; moveTo(+1, 0); break;
      case 'Enter':
        if (multiline && e.shiftKey) return; // Shift+Enter: a new line in a multi-line value
        moveTo(+1, 0);
        break;
      case 'Tab':        moveTo(0, e.shiftKey ? -1 : +1); break;
      case 'Home':
        if (caret && !e.ctrlKey) return; // caret: native line-start
        focusAt(e.ctrlKey ? 0 : displayPosOf(f.rowIdx), order[0]);
        break;
      case 'End':
        if (caret && !e.ctrlKey) return; // caret: native line-end
        focusAt(e.ctrlKey ? lastPos() : displayPosOf(f.rowIdx), order[lastCol()]);
        break;
      case 'PageUp':   focusAt(Math.max(0, displayPosOf(f.rowIdx) - pageRows), f.colName); break;
      case 'PageDown': focusAt(Math.min(lastPos(), displayPosOf(f.rowIdx) + pageRows), f.colName); break;
      case 'Insert':
        e.preventDefault();
        if (canInsert()) insertDraftAboveFocused();
        break;
      case 'Escape':
        e.preventDefault();
        if (isDraftRow(f.rowIdx)) {
          // Discard the whole pending new row. Empty → silently; with data →
          // confirm first (so an accidental Esc can't drop typed values).
          const slot = draftSlot(f.rowIdx);
          if (focusedDraftIsEmpty(f)) {
            discardDraft(slot);
          } else {
            const el = e.currentTarget;
            el?.blur?.(); // release focus so a 2nd Esc hits the dialog, not this cell
            setDiscardConfirm({ slot, el });
          }
        } else {
          setEditText(cellAsText(cellValue(f.rowIdx, f.colName))); // revert input
          setEditTouched(false);
          setFocusedCell(null);
          emitPreview();
        }
        break;
      default: /* let typing through */
    }
  };

  // Auto scroll-into-view when focus moves off-screen — without this, the
  // virtualised grid would unmount the focused row's input.
  createEffect(() => {
    const f = focusedCell();
    if (f) ensureRowVisible(displayPosOf(f.rowIdx));
  });

  // Numeric column names (driven by column type), pre-computed for fast lookup.
  const numericNames = createMemo(() => {
    const out = new Set();
    for (const c of props.columns ?? []) {
      if (isNumericType(c.type)) out.add(c.name);
    }
    return out;
  });

  const isNumeric    = (name) => numericNames().has(name);
  const shouldFormat = (name) =>
    props.thousandSep && isNumeric(name) && !excludedSet().has(name);

  // Temporal columns → always reformat the display (DATE → YYYY-MM-DD,
  // DATETIME/TIMESTAMP → YYYY-MM-DD HH:mm:ss). name → 'date' | 'datetime'.
  const temporalNames = createMemo(() => {
    const m = new Map();
    for (const c of props.columns ?? []) { const k = temporalKind(c.type); if (k) m.set(c.name, k); }
    return m;
  });
  const temporalOf = (name) => temporalNames().get(name);

  // Enum columns → the allowed-value list (for a <select> in edit mode). Keyed
  // by column name; null for non-enum types. The data grid carries the full
  // `enum('a','b')` type already; query-result columns get the real type only
  // when the backend marks the result editable (so this is empty otherwise).
  const enumNames = createMemo(() => {
    const m = new Map();
    for (const c of props.columns ?? []) { const v = enumValues(c.type); if (v) m.set(c.name, v); }
    return m;
  });
  const enumOf = (name) => enumNames().get(name) || null;

  // A column is editable unless its metadata explicitly says otherwise. The
  // table-data grid omits the flag (every non-PK column edits); the query-result
  // grid sets `editable:false` on read-only expression / sub-select columns so
  // they render but can't be changed even in edit mode.
  // A generated (computed) column is never writable (Error 3105), in either grid.
  const editableCol = (name) => {
    const it = colByName().get(name);
    return !it || (it.col.editable !== false && !isGeneratedExtra(it.col.extra));
  };

  // Binary values that aren't valid UTF-8 can't travel as JSON text — they
  // arrive with U+FFFD in place of the bytes. Such a cell can't be edited as
  // text (the shown value isn't the stored one), and a row whose KEY is such a
  // value can't be targeted at all (WHERE would match nothing), so it's
  // read-only and not selectable for delete. The backend also reports a key
  // that matches no row as an error rather than success.
  const isBinaryCol = (name) => isBinaryType(colByName().get(name)?.col?.type);
  const lossyBinary = (rowIdx, name) => {
    if (isDraftRow(rowIdx) || !isBinaryCol(name)) return false;
    const v = cellValue(rowIdx, name);
    return typeof v === 'string' && v.includes(String.fromCharCode(0xfffd)); // U+FFFD REPLACEMENT CHARACTER
  };
  const rowKeyLossy = (rowIdx) => pkCols().some((n) => lossyBinary(rowIdx, n));

  // ── Per-column INSERT roles (new-row drafts) ──────────────────────────────
  // A column is auto-filled by the DB (auto-increment, generated/computed) and
  // must be OMITTED from the INSERT + rendered read-only on a draft row. A
  // natural-key PK is NOT read-only on a draft — the user must type it.
  const insertAutoFilled = (name) => {
    const it = colByName().get(name);
    if (!it) return true;
    return !!it.col.autoIncrement || isGeneratedExtra(it.col.extra);
  };
  // Editable on a DRAFT row: anything not auto-filled and not a read-only
  // (query-result expression/alias) column. Unlike a data row, the PK is open.
  const draftCellEditable = (name) => !insertAutoFilled(name) && editableCol(name);
  // Required on insert: editable, NOT NULL, and no DEFAULT (so the DB can't fill
  // it). These must be provided or the INSERT is blocked client-side.
  const requiredForInsert = (name) => {
    const it = colByName().get(name);
    if (!it || !draftCellEditable(name)) return false;
    const c = it.col;
    const hasDefault = c.default !== null && c.default !== undefined;
    return !c.nullable && !hasDefault;
  };
  // Unified "can edit this cell in this row" used by the render + persist paths.
  // PK columns ARE editable; the host wraps the rename in a confirmation flow
  // (FK-references modal) before the change is staged.
  const cellIsEditable = (rowIdx, name) =>
    isDraftRow(rowIdx) ? draftCellEditable(name)
                       : editableCol(name) && !lossyBinary(rowIdx, name) && !rowKeyLossy(rowIdx);

  // ── Display string ──────────────────────────────────────────────
  // The exact text a cell shows. Shared by the row render *and* the auto-fit
  // measurer so the two can never drift (a DATETIME is 19 chars; a number with
  // thousandSep on is wider than its raw form — measuring the raw cell would
  // mis-size both).
  const displayString = (name, cell) => {
    if (cell === null || cell === undefined) return '';
    if (shouldFormat(name)) return formatNumberWithSep(cell);
    const tk = temporalOf(name);
    if (tk) return formatTemporal(cell, tk);
    return stringifyCell(cell);
  };

  // ── Auto-fit (one-shot) column sizing ──────────────────────────
  // Measures displayed cell content and returns a { colName: width } map sized
  // to the 90th-percentile cell width (clamped to [MIN_WIDTH, MAX_WIDTH], floored
  // at the header label). Columns whose sampled cells are MOSTLY null/empty drop
  // the header-name floor and shrink to just the NULL tag + header icons, so a
  // long column name can't widen a mostly-blank column. Exposed to the parent via
  // registerAutoFit(); the parent applies the widths (persist to colprefs for
  // data tabs, an ephemeral per-result map for query results).
  let measureCtx = null;
  let cellFontCache = null;
  let headerFontCache = null;
  const fontOf = (el, fallback) => {
    if (!el) return fallback;
    const cs = getComputedStyle(el);
    return cs.fontSize ? `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}` : fallback;
  };
  const measureWidth = (text, font) => {
    if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
    measureCtx.font = font;
    return measureCtx.measureText(text).width;
  };
  const computeAutoFitWidths = () => {
    // Read the real rendered fonts once (so theme / zoom / mono family match),
    // with hardcoded fallbacks if no cell/header is on screen yet.
    if (!cellFontCache)   cellFontCache   = fontOf(scrollerRef?.querySelector('.grid-cell .mono'), '12.5px monospace');
    if (!headerFontCache) headerFontCache = fontOf(headerEls.values().next().value, '600 12px sans-serif');
    const cellFont = cellFontCache, headerFont = headerFontCache;

    // Header chrome depends on which icons this grid actually renders.
    const headerChrome = HEADER_BASE
      + (canFilter() ? FILTER_ICON_W : 0)
      + (canSort()   ? SORT_ICON_W   : 0);

    const rows  = props.rows ?? [];
    const n     = Math.min(AUTOFIT_SAMPLE, rows.length);
    const nullW = measureWidth('NULL', cellFont);
    const out   = {};
    for (const name of orderNames()) {
      const info = colByName().get(name);
      if (!info) continue;
      const idx = info.naturalIdx;
      const widths = [];
      let empty = 0;
      for (let r = 0; r < n; r++) {
        const cell = rows[r]?.[idx];
        if (cell === null || cell === undefined) { widths.push(nullW); empty++; continue; }
        const s = displayString(name, cell);
        widths.push(s === '' ? 0 : measureWidth(s, cellFont));
        // null / empty-string / zero all count toward "mostly empty".
        if (s === '' || isZeroish(cell)) empty++;
      }
      // "Mostly empty" → ignore the column NAME and shrink to the NULL tag +
      // header icons (the name truncates; the hover tooltip still shows it).
      const mostlyEmpty = n > 0 && empty > n * AUTOFIT_EMPTY_PCT;
      let content = nullW + CELL_PAD;     // mostly-empty default: room for the NULL tag
      let headerW = headerChrome;         // mostly-empty default: icons only (name truncates)
      if (!mostlyEmpty) {
        widths.sort((a, b) => a - b);
        const p90 = widths.length
          ? widths[Math.min(widths.length - 1, Math.floor(0.9 * widths.length))]
          : 0;
        content = p90 + CELL_PAD;
        headerW = headerChrome + measureWidth(info.col.name, headerFont);
        if (props.showTypes !== false && info.col.type) headerW += measureWidth(info.col.type, cellFont) + 6;
      }
      out[name] = Math.round(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.max(content, headerW))));
    }
    return out;
  };

  // How many display positions are pinned (always at the front of orderNames
  // — Browser computes this).
  const pinnedCount = createMemo(
    () => orderNames().filter((n) => pinnedSet().has(n)).length
  );

  const range = createMemo(() => {
    const first   = Math.max(0, Math.floor(scrollTop() / ROW_HEIGHT) - OVERSCAN);
    const visible = Math.ceil(viewportH() / ROW_HEIGHT) + OVERSCAN * 2;
    const last    = Math.min(displayCount(), first + visible);
    return { first, last };
  });

  const onScroll = () => {
    setScrollTop(scrollerRef.scrollTop);
    setScrollLeft(scrollerRef.scrollLeft); // also reposition the column tooltip
  };

  // Column tooltip anchor: full name + type positioned just above the hovered
  // column's header cell. Reads the header's viewport rect, so `position:fixed`
  // escapes the scroller's overflow clipping. Recomputes when the hovered
  // column changes or the header slides horizontally (scrollLeft).
  const colTip = createMemo(() => {
    scrollLeft();
    const name = hoverCol();
    if (!name) return null;
    const el = headerEls.get(name);
    const info = colByName().get(name);
    if (!el || !info) return null;
    const r = el.getBoundingClientRect();
    return { name, type: info.col.type, left: r.left, top: r.top };
  });

  onMount(() => {
    setViewportH(scrollerRef.clientHeight);
    const ro = new ResizeObserver(() => setViewportH(scrollerRef.clientHeight));
    ro.observe(scrollerRef);
    onCleanup(() => ro.disconnect());
  });

  const cellExtraClass = (displayPos) => {
    if (displayPos >= pinnedCount()) return '';
    return displayPos === pinnedCount() - 1
      ? 'grid-cell-pinned grid-cell-pinned-last'
      : 'grid-cell-pinned';
  };

  // ── Column resize ────────────────────────────────────────────
  // Captured on the resize handle; we attach window-level move/up listeners
  // so dragging continues even when the cursor leaves the cell.
  const [draftWidth, setDraftWidth] = createSignal(null);
  const [draftCol, setDraftCol]     = createSignal(null);

  const onResizeMouseDown = (name, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthOf(name);
    setDraftCol(name);
    setDraftWidth(startW);

    const onMove = (ev) => {
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startW + (ev.clientX - startX)));
      setDraftWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      const finalW = draftWidth();
      const col    = draftCol();
      setDraftWidth(null);
      setDraftCol(null);
      if (col != null && finalW != null && finalW !== startW) {
        props.onResize?.(col, finalW);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  };

  // Width during render — uses draft (during drag) for instant feedback.
  const liveWidthOf = (name) => {
    if (draftCol() === name && draftWidth() != null) return draftWidth();
    return widthOf(name);
  };

  // Cumulative left offsets per display position, computed ONCE per
  // (orderNames, widths, draft) change instead of per-cell. Avoids the
  // previous O(N²·M) loop where every sticky body cell recomputed its
  // own offset during virtualized render.
  const liveLeftOffsets = createMemo(() => {
    const names = orderNames();
    const offsets = new Array(names.length);
    let acc = 0;
    for (let i = 0; i < names.length; i++) {
      offsets[i] = acc;
      acc += liveWidthOf(names[i]);
    }
    // Stash the total at index `length` so liveTableWidth() can reuse it.
    offsets.total = acc;
    return offsets;
  });
  const liveTableWidth = () => liveLeftOffsets().total;

  // ── Row-number gutter ────────────────────────────────────────
  // A sticky leftmost "#" column showing each row's ordinal. `startIndex` offsets
  // the numbering (the page offset for table data; 0 for query results). The
  // gutter width grows with the largest number shown so big offsets don't clip.
  // Pinned columns' sticky-left offsets and the table width all shift right by
  // the gutter width; the gutter itself isn't a data column, so selection /
  // reorder / display positions are unaffected.
  const startIndex = () => props.startIndex || 0;
  const gutterWidth = createMemo(() => {
    const maxNum = startIndex() + Math.max(displayCount(), 1);
    return Math.max(40, 16 + String(maxNum).length * 9);
  });
  const fullWidth = () => gutterWidth() + liveTableWidth();

  const liveCellStyle = (name, displayPos) => {
    const base = { width: `${liveWidthOf(name)}px` };
    if (displayPos < pinnedCount()) {
      return { ...base, position: 'sticky', left: `${gutterWidth() + liveLeftOffsets()[displayPos]}px` };
    }
    return base;
  };

  // ── Column reorder (HTML5 drag-and-drop) ─────────────────────
  const [dragSrc, setDragSrc]     = createSignal(null); // column name being dragged
  const [dragOver, setDragOver]   = createSignal(null); // hover position (display index)

  // Pinned columns are NOT draggable while the pin feature is in effect — they
  // belong to the pinned region and reorder there is meaningless for the user.
  // Reorder is also off entirely when no onReorder handler is wired (query results).
  const isDraggable = (name) => canReorder() && !pinnedSet().has(name);

  // Header tooltip built from the actions actually available on this grid, so
  // the query-result header doesn't claim a left-click filter it can't do.
  const headerTitle = (name) => {
    const parts = [];
    if (canFilter()) parts.push('Left-click to filter');
    if (typeof props.onHeaderContextMenu === 'function' || selectable()) parts.push('right-click for menu');
    if (isDraggable(name)) parts.push('drag to reorder');
    return parts.join(', ');
  };

  const onDragStart = (name, e) => {
    if (!isDraggable(name)) { e.preventDefault(); return; }
    setDragSrc(name);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers need a dataTransfer payload to start the drag.
    try { e.dataTransfer.setData('text/plain', name); } catch (_) {}
  };

  const onDragOverCol = (displayPos, e) => {
    if (!dragSrc()) return;
    if (displayPos < pinnedCount()) return; // can't drop into pinned region
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(displayPos);
  };

  const onDropCol = (displayPos, e) => {
    if (!dragSrc()) return;
    e.preventDefault();
    if (displayPos < pinnedCount()) { setDragSrc(null); setDragOver(null); return; }
    const order = [...orderNames()];
    const fromIdx = order.indexOf(dragSrc());
    if (fromIdx < 0) { setDragSrc(null); setDragOver(null); return; }
    order.splice(fromIdx, 1);
    // After removal indices shift; adjust target if needed.
    let to = displayPos;
    if (fromIdx < displayPos) to -= 1;
    order.splice(to, 0, dragSrc());
    setDragSrc(null);
    setDragOver(null);
    props.onReorder?.(order);
  };

  const onDragEnd = () => {
    setDragSrc(null);
    setDragOver(null);
  };

  return (
    <div class="grid">
      {/* In-grid find bar (Ctrl+F) — floats over the top-right of the grid. */}
      <Show when={findOpen()}>
        <div class="grid-find">
          <input
            ref={findInputRef}
            class="input grid-find-input mono"
            placeholder="Find in results…"
            value={findText()}
            onInput={onFindInput}
            onKeyDown={onFindKey}
          />
          <span class="grid-find-count">
            {findMatches().length ? `${findIdx() + 1} / ${findMatches().length}` : (findQuery().trim() ? 'no matches' : '')}
          </span>
          <button class="grid-find-btn" title="Previous match (Shift+Enter)" disabled={!findMatches().length} onClick={() => gotoMatch(-1)}>▲</button>
          <button class="grid-find-btn" title="Next match (Enter)" disabled={!findMatches().length} onClick={() => gotoMatch(+1)}>▼</button>
          <button class="grid-find-btn" title="Close (Esc)" onClick={closeFind}>×</button>
        </div>
      </Show>
      <div class={`grid-scroller ${selectable() ? 'grid-selectable' : ''}`} ref={scrollerRef}
           tabindex={selectable() ? -1 : undefined} onKeyDown={onGridKeyDown}
           onScroll={onScroll} onMouseLeave={() => setHoverCol(null)}>
        {/* Header */}
        <div class="grid-header" role="row" style={{ width: `${fullWidth()}px` }}>
          {/* The header gutter cell is `position: sticky; left: 0;` (via the
              .grid-gutter rule) — but because .grid-header is itself sticky
              (vertical), the inner sticky's containing block is .grid-header,
              not .grid-scroller. So when the user scrolls horizontally, the
              header gutter drifts left with the header instead of pinning. We
              compensate with a transform that offsets the cell by scrollLeft
              — the parent shifts left by N px, the child shifts right by N px,
              and visually it stays in place. Body row gutters work fine because
              their parent (.grid-row) is `position: absolute`, not sticky, so
              the inner sticky resolves against .grid-scroller as intended. */}
          <div class="grid-cell grid-th grid-gutter"
               style={{
                 width: `${gutterWidth()}px`,
                 transform: scrollLeft() ? `translate3d(${scrollLeft()}px, 0, 0)` : undefined,
               }}
               title="Row number">#</div>
          <For each={orderNames()}>
            {(name, displayPos) => {
              const item = () => colByName().get(name);
              return (
                <Show when={item()}>
                  <div
                    class={`grid-cell grid-th ${canFilter() ? 'grid-th-clickable ' : ''}${cellExtraClass(displayPos())}${isNumeric(name) ? ' grid-th-numeric' : ''}${hiddenSet().has(name) ? ' grid-th-hidden' : ''}${draftCol() === name ? ' grid-th-resizing' : ''}`}
                    classList={{
                      'grid-th-dragging':  dragSrc() === name,
                      'grid-th-drop-left': dragOver() === displayPos() && dragSrc() !== name,
                    }}
                    role="columnheader"
                    ref={(el) => el && headerEls.set(name, el)}
                    title={headerTitle(name)}
                    style={liveCellStyle(name, displayPos())}
                    draggable={isDraggable(name)}
                    onMouseEnter={() => setHoverCol(name)}
                    // Shift+click opens the filter (the ⌕ icon below is the
                    // one-click opener). A plain click on the header selects the
                    // whole column in a browse/selectable grid (no-op while
                    // editing — the sort/filter/resize icons stopPropagation).
                    onClick={(e) => {
                      if (e.shiftKey) { props.onHeaderClick?.(item().col, e); return; }
                      if (selectable()) selectColumn(displayPos());
                    }}
                    onContextMenu={(e) => onHeaderMenu(item().col, e)}
                    onDragStart={(e) => onDragStart(name, e)}
                    onDragOver={(e) => onDragOverCol(displayPos(), e)}
                    onDrop={(e) => onDropCol(displayPos(), e)}
                    onDragEnd={onDragEnd}
                  >
                    <span class={`pk-marker ${item().col.isPrimary ? 'pk' : ''}`} />
                    <span class="col-name">{item().col.name}</span>
                    <Show when={isNumeric(name) && excludedSet().has(name) && props.thousandSep}>
                      <span
                        class="grid-th-excl"
                        title="Excluded from thousand-separator formatting"
                      >Ø</span>
                    </Show>
                    <Show when={props.showTypes !== false}>
                      <span class="col-type mono muted">{item().col.type}</span>
                    </Show>
                    <Show when={canFilter()}>
                      <span class="grid-th-filter-icon" title="Filter (or Shift+click the header)"
                            onClick={(e) => { e.stopPropagation(); props.onHeaderClick?.(item().col, e); }}>⌕</span>
                    </Show>
                    {/* Sort icon — clickable, cycles asc → desc → off.
                        Ctrl+click adds as another sort key; plain click on a
                        new column resets to just that column (handled in caller).
                        Only shown when a sort handler is wired (table data, not
                        the static query-result grid). */}
                    <Show when={canSort()}>
                    {(() => {
                      const s = sortIndex().get(name);
                      const arrow = !s ? '⇅' : s.dir === 'asc' ? '▲' : '▼';
                      const showRank = s && sortCount() > 1;
                      return (
                        <span
                          class={`grid-th-sort ${s ? 'active' : ''}`}
                          title={
                            s
                              ? `Sorted ${s.dir} (${s.order}/${sortCount()}) — click to cycle, Ctrl+click another column to add`
                              : 'Click to sort ascending, Ctrl+click to add as another sort key'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onSortToggle?.(name, e.ctrlKey || e.metaKey);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <span class="grid-th-sort-arrow">{arrow}</span>
                          <Show when={showRank}>
                            <span class="grid-th-sort-rank">{s.order}</span>
                          </Show>
                        </span>
                      );
                    })()}
                    </Show>
                    {/* Resize handle */}
                    <span
                      class="grid-th-resize"
                      onMouseDown={(e) => onResizeMouseDown(name, e)}
                      onClick={(e) => e.stopPropagation()}
                      title="Drag to resize"
                    />
                  </div>
                </Show>
              );
            }}
          </For>
        </div>

        <Show when={displayCount() > 0} fallback={<div class="grid-empty muted">No rows.</div>}>
          <div
            class="grid-spacer"
            style={{
              height: `${displayCount() * ROW_HEIGHT}px`,
              width:  `${fullWidth()}px`,
              position: 'relative',
            }}
          >
            {/* Keyed by absolute row index (data or draft) so an in-place edit
                / optimistic mutate updates without remounting. translateY reads
                displayPosOf so a row shifts down when a draft is inserted above. */}
            <For each={displayOrder().slice(range().first, range().last)}>{(idx) => {
              const draftRow = isDraftRow(idx);
              const pos = () => displayPosOf(idx); // reactive: shifts when a draft is inserted above
              const activeRow = () => selectable() && selFocus() && selFocus().r === idx;
              const rowSelected = () => isRowSelected(idx);
              // Per-row staged-edit key, computed per row (not per cell). Re-derived
              // when props.rows changes — this row component is keyed by display
              // index, so after a sort it shows a different record; '' when not editing.
              const rowKey = createMemo(() => (props.editable ? rowDirtyKey(idx) : ''));
              return (
                <div
                  class={`grid-row${draftRow ? ' grid-row-draft' : ''}${pos() % 2 ? ' grid-row-odd' : ''}${activeRow() ? ' grid-row-active' : ''}${rowSelected() ? ' grid-row-selected' : ''}`}
                  role="row"
                  style={{
                    transform: `translateY(${pos() * ROW_HEIGHT}px)`,
                    height:    `${ROW_HEIGHT}px`,
                    width:     `${fullWidth()}px`,
                  }}
                >
                  <div
                    class={`grid-cell grid-gutter${props.editable && !draftRow && props.getRowKey ? ' grid-gutter-clickable' : ''}`}
                    style={{ width: `${gutterWidth()}px` }}
                    title={props.editable && !draftRow && props.getRowKey ? 'Click to select row · Shift+click range · Ctrl/Cmd+click toggle' : ''}
                    onClick={(e) => onGutterClick(idx, e)}
                  >
                    {startIndex() + pos() + 1}
                  </div>
                  <For each={orderNames()}>
                    {(name, displayPos) => {
                      const item = colByName().get(name);
                      if (!item) return null;
                      // Reactive — so an in-place data edit or draft value change
                      // updates the cell without the row remounting.
                      const cell = () => cellValue(idx, name);
                      // On a draft row, an auto-filled column (auto-increment /
                      // generated) shows "(auto)" and is read-only.
                      const draftAuto = draftRow && insertAutoFilled(name);
                      const displayValue = () => (draftAuto ? '(auto)' : displayString(name, cell()));
                      const focusedHere = () =>
                        focusedCell() && focusedCell().rowIdx === idx && focusedCell().colName === name;
                      const cellInteractive = () => props.editable;
                      const editableCell    = () => props.editable && cellIsEditable(idx, name);
                      // What the user has actually edited in this row, if any.
                      const bufferedValue = () => bufferedRow.rowIdx === idx
                        ? bufferedRow.changes[name]
                        : undefined;
                      const isBuffered = () => bufferedValue() !== undefined;
                      // Long free-text → collapsed preview + expand-to-modal.
                      const fullText = () => {
                        const v = isBuffered() ? bufferedValue() : cell();
                        return v == null ? '' : String(v);
                      };
                      const isLong = () =>
                        !temporalOf(name) && !isNumeric(name) && fullText().length > LONG_TEXT;
                      // A required column left empty on a draft row → flag it.
                      const draftMissing = () => {
                        if (!draftRow || !requiredForInsert(name)) return false;
                        const d = draftAt(idx);
                        const has = d && Object.prototype.hasOwnProperty.call(d.values, name);
                        const v = has ? d.values[name] : undefined;
                        return v === undefined || v === null || String(v) === '';
                      };
                      // Date-picker helper for temporal edit cells: pick a date
                      // (no time) and splice it into editText, leaving the time
                      // portion for the user to type.
                      let dateRef;
                      const onDatePick = (picked) => {
                        if (!picked) return;
                        if (temporalOf(name) === 'date') { editInput(picked); emitPreview(); return; }
                        const tm = editText().match(/(\d{2}:\d{2}:\d{2})/);
                        editInput(`${picked} ${tm ? tm[1] : '00:00:00'}`);
                        emitPreview();
                      };
                      return (
                        <div
                          class={`grid-cell ${cellExtraClass(displayPos())}${isNumeric(name) ? ' grid-cell-numeric' : ''}${hiddenSet().has(name) ? ' grid-cell-hidden' : ''}${cellInteractive() ? ' grid-cell-editable' : ''}${focusedHere() ? ' grid-cell-editing' : ''}${isBuffered() ? ' grid-cell-buffered' : ''}${props.editable && dirty().has(`${rowKey()}::${name}`) ? ' grid-cell-dirty' : ''}${isLong() && !focusedHere() ? ' grid-cell-haslong' : ''}${inSelection(idx, displayPos()) ? ' grid-cell-selected' : ''}${isActiveCell(idx, displayPos()) ? ' grid-cell-active' : ''}${cellFindClass(idx + ':' + displayPos())}${flashCell() && flashCell().r === idx && flashCell().name === name ? ' grid-cell-copied' : ''}${ctrlHeld() && selectable() && isFk(name) && cellValue(idx, name) != null ? ' grid-cell-fk-clickable' : ''}${draftMissing() && !focusedHere() ? ' grid-cell-reqmissing' : ''}`}
                          role="cell"
                          title={editableCell()
                            ? `${cellTitle(cell())} — click or arrow keys to edit (Tab to next, Enter to next row)`
                            : cellTitle(cell())}
                          style={liveCellStyle(name, displayPos())}
                          onMouseEnter={() => { setHoverCol(name); onSelectExtend(idx, displayPos()); }}
                          onMouseDown={(e) => onCellMouseDown(idx, displayPos(), e)}
                          onContextMenu={(e) => onCellMenu(item.col, idx, displayPos(), e)}
                          onClick={async (e) => {
                            if (!cellInteractive()) return;
                            // A MOUSE click into an editable cell means "edit text here",
                            // so switch to caret mode: Left/Right (and Ctrl+Left/Right for
                            // word jumps) move the caret instead of navigating cells. (Arrow-
                            // key navigation INTO a cell never runs this path, so it stays in
                            // nav mode — text selected, arrows move between cells.)
                            const f = focusedCell();
                            if (f && f.rowIdx === idx && f.colName === name) {
                              // Clicking inside the already-focused cell to place a caret.
                              if (editableCell()) setCaretMode(true);
                              return;
                            }
                            await focusCell(idx, name);
                            if (editableCell()) setCaretMode(true);
                          }}
                        >
                          {/* Three render branches:
                              1. Focused & editable → <input> (navigates + types)
                              2. Focused & PK       → focusable read-only host so
                                 arrow keys still route through onCellInputKey
                                 (we just don't render an input; typing is a no-op)
                              3. Unfocused          → plain text / NULL tag */}
                          {(() => {
                            // On a draft row: auto-filled cols show "(auto)"; a
                            // not-yet-typed editable col is blank (vs the NULL tag,
                            // which is reserved for an explicitly-entered NULL).
                            const draftUnset = () => {
                              if (!draftRow || draftAuto) return false;
                              const d = draftAt(idx);
                              return !(d && Object.prototype.hasOwnProperty.call(d.values, name));
                            };
                            const valueNode = () => (
                              <Show when={draftAuto} fallback={
                                <Show when={draftUnset()} fallback={
                              <Show when={isBuffered() && bufferedValue() === null}
                                    fallback={
                                      <Show when={cell() === null && !isBuffered()}
                                            fallback={
                                              <Show when={isLong()} fallback={<span class="mono">{
                                                isBuffered() ? String(bufferedValue()) : displayValue()
                                              }</span>}>
                                                <span class="mono grid-cell-preview">{collapsePreview(fullText(), 200)}</span>
                                                <button class="grid-cell-expand" title="Show full text"
                                                        onClick={(e) => { e.stopPropagation(); openExpand(idx, name, isBuffered() ? bufferedValue() : cell(), editableCell()); }}>⤢</button>
                                              </Show>
                                            }>
                                        <span class="null-tag">NULL</span>
                                      </Show>
                                    }>
                                <span class="null-tag">NULL</span>
                              </Show>
                                }>
                                  <span class="grid-cell-draft-empty" />
                                </Show>
                              }>
                                <span class="grid-cell-auto muted mono">(auto)</span>
                              </Show>
                            );
                            return (
                              <Show when={focusedHere() && editableCell()} fallback={
                                <Show when={focusedHere() && !editableCell()} fallback={valueNode()}>
                                  <div
                                    class="grid-cell-readfocus"
                                    tabindex="0"
                                    ref={(el) => el && setTimeout(() => el.focus(), 0)}
                                    onKeyDown={onCellInputKey}
                                    title={lossyBinary(idx, name) || rowKeyLossy(idx)
                                      ? 'Binary value that can\'t be shown exactly — it can\'t be edited here. Use arrow keys to move.'
                                      : isGeneratedExtra(item.col.extra)
                                        ? 'Generated column — computed by the server, read-only. Use arrow keys to move.'
                                        : isPKColumn(name)
                                          ? 'Primary key — read-only. Use arrow keys to move.'
                                          : 'Read-only column — use arrow keys to move.'}
                                  >
                                    {valueNode()}
                                  </div>
                                </Show>
                              }>
                                <Show
                                  when={enumOf(name)}
                                  fallback={
                                <Show
                                  when={temporalOf(name)}
                                  fallback={
                                    <>
                                    <Show when={editMultiline()} fallback={
                                    <input
                                      class="grid-cell-input mono"
                                      list={isFk(name) ? fkListId(name) : undefined}
                                      title="Type NULL for SQL NULL; 'NULL' (in quotes) for the text NULL"
                                      ref={(el) => el && setTimeout(() => { el.focus(); el.select(); if (isFk(name)) ensureFkOptions(name); }, 0)}
                                      value={editText()}
                                      onInput={(e) => { editInput(e.currentTarget.value); emitPreview(); }}
                                      onKeyDown={onCellInputKey}
                                      onBlur={() => {
                                        // Persist the typed text into the row buffer; the actual
                                        // server flush is triggered by the parent (Commit/Rollback)
                                        // or by moving to a different row.
                                        persistCurrentEdit();
                                      }}
                                    />
                                    }>
                                      {/* A value with line breaks edits in a one-row textarea —
                                          an <input> would strip them on the first keystroke.
                                          Shift+Enter adds a line; Enter still moves down. */}
                                      <textarea
                                        class="grid-cell-input mono"
                                        rows="1"
                                        style={{ resize: 'none', 'white-space': 'pre', overflow: 'hidden' }}
                                        title="Multi-line value — Shift+Enter for a new line, F2 to move the caret, ⤢ for the full editor"
                                        ref={(el) => el && setTimeout(() => { el.focus(); el.select(); }, 0)}
                                        value={editText()}
                                        onInput={(e) => { editInput(e.currentTarget.value); emitPreview(); }}
                                        onKeyDown={onCellInputKey}
                                        onBlur={() => persistCurrentEdit()}
                                      />
                                    </Show>
                                    {/* FK lookup suggestions — pick a valid parent value (still
                                        free-typeable, since the sample may not be exhaustive). */}
                                    <Show when={isFk(name) && (fkOpts()[name] || []).length > 0}>
                                      <datalist id={fkListId(name)}>
                                        <For each={fkOpts()[name]}>{(v) => <option value={v} />}</For>
                                      </datalist>
                                    </Show>
                                    </>
                                  }
                                >
                                  {/* Temporal edit: a text field (type the time) + a 📅 button
                                      that opens the native date picker and sets only the date
                                      part — arrow keys keep navigating cells (not hijacked by a
                                      native date input). */}
                                  <div class="grid-cell-dtedit">
                                    <input
                                      class="grid-cell-input mono"
                                      ref={(el) => el && setTimeout(() => { el.focus(); el.select(); }, 0)}
                                      value={editText()}
                                      onInput={(e) => { editInput(e.currentTarget.value); emitPreview(); }}
                                      onKeyDown={onCellInputKey}
                                      onBlur={() => persistCurrentEdit()}
                                    />
                                    <button class="grid-cell-cal" title="Pick date"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => dateRef?.showPicker?.()}>📅</button>
                                    <input type="date" class="grid-cell-cal-hidden" tabindex="-1"
                                           ref={(el) => { dateRef = el; }}
                                           value={(editText().match(/^\d{4}-\d{2}-\d{2}/) || [''])[0]}
                                           onInput={(e) => onDatePick(e.currentTarget.value)} />
                                  </div>
                                </Show>
                                  }
                                >
                                  {/* Enum edit: a <select> of the declared members.
                                      Arrow/Tab/Enter still navigate cells (onCellInputKey);
                                      pick with the mouse or type-ahead. (NULL) appears for
                                      a nullable column; an out-of-range current value is
                                      preserved as a temporary option so opening the
                                      dropdown can't silently change it. */}
                                  <select
                                    class="grid-cell-input grid-cell-enum mono"
                                    ref={(el) => el && setTimeout(() => el.focus(), 0)}
                                    value={editText()}
                                    onChange={(e) => { editInput(e.currentTarget.value); persistCurrentEdit(); emitPreview(); }}
                                    onKeyDown={onCellInputKey}
                                  >
                                    {/* Option values use the editor-text form (editTextOf), so a
                                        member literally named NULL can't be confused with (NULL). */}
                                    <Show when={item.col.nullable}>
                                      <option value="NULL">(NULL)</option>
                                    </Show>
                                    <Show when={editText() !== 'NULL' && !enumOf(name).map(editTextOf).includes(editText())}>
                                      <option value={editText()}>{editText() === '' ? '(empty)' : editText()} — current</option>
                                    </Show>
                                    <For each={enumOf(name)}>
                                      {(v) => <option value={editTextOf(v)}>{v}</option>}
                                    </For>
                                  </select>
                                </Show>
                              </Show>
                            );
                          })()}
                        </div>
                      );
                    }}
                  </For>
                </div>
              );
            }}</For>
          </div>
        </Show>
      </div>

      {/* Status footer — total rows (or the caller's footerRows, e.g. a pager)
          on the left, live Excel-style stats for the current selection on the
          right (selectable grids only). */}
      <Show when={selectable() || props.footerRows}>
        <div class="grid-footer mono">
          <Show when={props.footerRows} fallback={
            <span class="grid-footer-rows">
              {formatNumberWithSep(displayCount())}
              <Show
                when={props.totalRows != null && props.totalRows !== displayCount()}
                fallback={<> row{displayCount() === 1 ? '' : 's'}</>}
              >
                {' '}of {formatNumberWithSep(props.totalRows)} rows <span class="grid-footer-dim">(filtered)</span>
              </Show>
            </span>
          }>
            {props.footerRows()}
          </Show>
          <div class="spacer" />
          <Show when={selectable() && selStats()}>
            <span class="grid-footer-sel">{formatNumberWithSep(selStats().cells)} selected</span>
            <span class="grid-footer-dim">{formatNumberWithSep(selStats().rows)}×{formatNumberWithSep(selStats().cols)}</span>
            <Show
              when={!selStats().capped}
              fallback={<span class="grid-footer-stat muted">too large for live totals</span>}
            >
              <span class="grid-footer-stat"><b>Count</b> {formatNumberWithSep(selStats().count)}</span>
              <Show when={selStats().numCount > 0}>
                <span class="grid-footer-stat"><b>Sum</b> {fmtNum(selStats().sum)}</span>
                <span class="grid-footer-stat"><b>Avg</b> {fmtNum(selStats().avg)}</span>
                <span class="grid-footer-stat"><b>Min</b> {fmtNum(selStats().min)}</span>
                <span class="grid-footer-stat"><b>Max</b> {fmtNum(selStats().max)}</span>
              </Show>
            </Show>
          </Show>
        </div>
      </Show>

      {/* Column tooltip — full name + type, floating above the hovered column's
          header. position:fixed (set in CSS) + viewport coords escape the
          scroller's overflow clipping. pointer-events:none so it never blocks. */}
      <Show when={colTip()}>
        <div
          class="grid-col-tip"
          style={{ left: `${colTip().left}px`, top: `${colTip().top}px` }}
        >
          <span class="grid-col-tip-name">{colTip().name}</span>
          <span class="grid-col-tip-type mono">{colTip().type}</span>
        </div>
      </Show>

      {/* Rich cell-value viewer — replaces the old long-text modal. Format
          detection (JSON / XML / image / hex / text), edit-and-write-back on
          editable grids, and the cell-to-cell diff angle (Pick → open another
          cell → Diff). Triggered by the cell's "expand" affordance, by
          right-click → View value, or by Space when a cell is focused. */}
      <CellValueViewer
        open={!!expanded()}
        onClose={() => setExpanded(null)}
        value={expandText()}
        label={expanded()?.name || ''}
        editable={!!expanded()?.editable}
        onApply={async (newText) => { setExpandText(newText); await saveExpand(); }}
      />

      {/* Discard-new-row confirmation (Esc on a draft that has data) */}
      <Show when={discardConfirm()}>
        <Modal open={true} onClose={cancelDiscard} title="Discard new row?">
          <p class="muted" style="font-size:13px;margin:0 0 16px;">
            This new row hasn’t been saved yet — the values you entered will be lost.
          </p>
          <div style="display:flex;gap:8px;justify-content:flex-end;">
            <button class="btn btn-ghost" onClick={cancelDiscard}>Keep editing</button>
            <button class="btn btn-danger" onClick={() => discardDraft(discardConfirm().slot)}>Discard row</button>
          </div>
        </Modal>
      </Show>

      {/* Copy / stats context menu (selectable query-result grid) */}
      <Show when={menu()}>
        <ContextMenu x={menu().x} y={menu().y} items={menu().items} onClose={() => setMenu(null)} />
      </Show>

      {/* Column stats — computed client-side over the loaded rows */}
      <Show when={stats()}>
        <Modal open={true} onClose={() => setStats(null)} title={`Column stats — ${stats().name}`}>
          <table class="col-stats">
            <tbody>
              <tr><td>Rows (loaded)</td><td class="mono">{fmtNum(stats().total)}</td></tr>
              <tr><td>Non-null</td><td class="mono">{fmtNum(stats().nonNull)}</td></tr>
              <tr><td>NULLs</td><td class="mono">{fmtNum(stats().nulls)}</td></tr>
              <tr><td>Distinct</td><td class="mono">{fmtNum(stats().distinct)}</td></tr>
              <Show when={stats().numeric && stats().nonNull > 0}>
                <tr><td>Min</td><td class="mono">{fmtNum(stats().min)}</td></tr>
                <tr><td>Max</td><td class="mono">{fmtNum(stats().max)}</td></tr>
                <tr><td>Sum</td><td class="mono">{fmtNum(stats().sum)}</td></tr>
                <tr><td>Average</td><td class="mono">{fmtNum(stats().avg)}</td></tr>
              </Show>
              <Show when={!stats().numeric && stats().nonNull > 0}>
                <tr><td>Min (lexical)</td><td class="mono">{collapsePreview(stats().sMin ?? '', 80)}</td></tr>
                <tr><td>Max (lexical)</td><td class="mono">{collapsePreview(stats().sMax ?? '', 80)}</td></tr>
              </Show>
            </tbody>
          </table>
        </Modal>
      </Show>
    </div>
  );
}

function stringifyCell(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  return s.length > 200 ? s.slice(0, 200) + '…' : s;
}

function cellTitle(v) {
  if (v === null || v === undefined) return 'NULL';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// A numeric zero (0, "0", "0.00", "-0", ".0") — counted alongside null/empty in
// auto-fit's "mostly empty" column test. Non-numeric strings (a literal "0abc",
// a date) are not zero.
function isZeroish(v) {
  if (typeof v === 'number') return v === 0;
  if (typeof v === 'string') {
    const t = v.trim();
    return t !== '' && /^-?\d*\.?\d+$/.test(t) && Number(t) === 0;
  }
  return false;
}
